import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Edit2,
  Save,
  Image as ImageIcon,
  Video,
  Upload,
  Loader2,
  Play,
  X,
  Download,
  Film,
  Pencil,
  History,
  Mic,
  Volume2,
  Plus,
  Clapperboard,
  Trash2,
  FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EditorTopNav } from '@/components/editorViewTabs/EditorTopNav';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { fetchMurfVoiceCatalog } from '@/lib/murfApi';
import { finalizeNarrationSegmentAudioRegeneration } from '@/lib/narrationSegmentAudioRegen';
import { mediaUrlForPlayback, mediaUrlWithCacheBust } from '@/lib/mediaUrls';
import { collectSceneMediaVersions } from '@/lib/sceneMediaVersions';
import {
  mergeNarrationSegmentIntoScenes,
  narrationImageGenerationResult,
  narrationProjectToStory,
  narrationSceneImageUrl,
  narrationSegmentsFromProject,
} from '@/lib/sceneGridNarrationAdapter';
import {
  SCENE_EDIT_PAGE_SIZE,
  fetchNarrationSceneEditShell,
  fetchNarrationSceneEditScenesPage,
  mapEditSegmentsToScenes,
  mergeSceneEditPages,
} from '@/lib/narrationSceneEditLoad';
import { DoodleCanvasModal } from '@/components/DoodleCanvasModal';
import { DialogueCameraDialog } from '@/components/scene/DialogueCameraDialog';
import { MovieDirectorPanel } from '@/components/MovieDirectorPanel';
import { StorySoundPanel, StorySoundToggle, useStorySoundGate } from '@/components/story/StorySoundPanel';
import { useMovieDirector } from '@/contexts/MovieDirectorContext';
import { NarrationAudioSettingsBar } from '@/components/NarrationAudioSettingsBar';
import { NarrationAudioSettingsPanel } from '@/components/NarrationAudioSettingsPanel';
import { NarrationVoicePickerDialog } from '@/components/NarrationVoicePickerDialog';
import {
  buildNarrationAudioApiPayload,
  narrationAudioSettingsFromInput,
} from '@/lib/narrationAudioSettings';
import { normalizeVoiceStyleForProvider } from '@/lib/ttsProviderStyles';
import { fetchVideoGenerationPrompt } from '@/lib/videoGenerationPrompt';
import { VideoGenerationPromptDialog } from '@/components/VideoGenerationPromptDialog';
import {
  billingProviderForVideo,
  fetchCreditQuote,
  formatCreditEstimate,
  sceneBillingDurationSeconds,
} from '@/lib/billingQuote';
import { formatApiError } from '@/lib/formatApiError';
import { useInsufficientCredits, handleInsufficientCreditsError } from '@/contexts/InsufficientCreditsContext';
import { SongMultishotTimelinePicker } from '@/components/SongMultishotTimelinePicker';
import {
  applyMultishotSelectionPatch,
  DEFAULT_MULTISHOT_PROMPT_TEMPLATE,
  multishotSelectionPayloadFromScene,
  sceneHasMultishotPicker,
} from '@/lib/songMultishotSelection';
import {
  songAnimationSettingsFromInput,
  songAnimationSettingsToApiPayload,
  songAnimationSpeedLabel,
  SONG_ANIMATION_SPEED_DEFAULT,
  MOTION_SPEED_SLIDER_MIN,
  MOTION_SPEED_SLIDER_MAX,
  songAnimationSpeedToSlider,
  songAnimationSpeedFromSlider,
} from '@/lib/songAnimationSettings';
import { isSongBasedNarrationProject } from '@/lib/songBasedVideoEditor';
import { Slider } from '@/components/ui/slider';
import {
  SCRIPT_LINE_KIND_LABELS,
  sceneActionLines,
  scriptDraftToPayload,
  scriptToDraft,
} from '@/lib/sceneScript';

import { TwinneeLogo } from '@/components/TwinneeLogo';
function segmentBarColorCss(entry) {
  if ((entry.kind || '').toLowerCase() === 'narration') return '#64748b';
  const id = entry.character_id || entry.speaker || '';
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 33 + id.charCodeAt(i)) % 360;
  return `hsl(${h} 52% 42%)`;
}

function collectDialogueCharacterIds(scene) {
  const ids = new Set();
  const script = scene?.audio_script;
  if (!Array.isArray(script)) return [];
  for (const seg of script) {
    if (!seg || String(seg.kind || '').toLowerCase() !== 'dialogue') continue;
    if (seg.character_id) ids.add(seg.character_id);
  }
  return Array.from(ids);
}

const CAMERA_MOTION_CHOICES = {
  nano_banana: [
    { value: 'static', label: 'Static' },
    { value: 'pan_left', label: 'Pan left' },
    { value: 'pan_right', label: 'Pan right' },
    { value: 'tilt_up', label: 'Tilt up' },
    { value: 'tilt_down', label: 'Tilt down' },
    { value: 'zoom_in', label: 'Zoom in' },
    { value: 'zoom_out', label: 'Zoom out' },
  ],
  minimax: [
    { value: 'auto', label: 'Auto' },
    { value: 'static', label: 'Static' },
    { value: 'dolly_in', label: 'Dolly in' },
    { value: 'dolly_out', label: 'Dolly out' },
    { value: 'pan_left', label: 'Pan left' },
    { value: 'pan_right', label: 'Pan right' },
  ],
  veo3: [
    { value: 'auto', label: 'Auto' },
    { value: 'static', label: 'Static' },
    { value: 'dolly_in', label: 'Dolly in' },
    { value: 'dolly_out', label: 'Dolly out' },
    { value: 'pan_left', label: 'Pan left' },
    { value: 'pan_right', label: 'Pan right' },
    { value: 'tilt_up', label: 'Tilt up' },
    { value: 'tilt_down', label: 'Tilt down' },
  ],
  runway: [
    { value: 'auto', label: 'Auto' },
    { value: 'static', label: 'Static' },
    { value: 'dolly_in', label: 'Dolly in' },
    { value: 'dolly_out', label: 'Dolly out' },
    { value: 'pan_left', label: 'Pan left' },
    { value: 'pan_right', label: 'Pan right' },
    { value: 'tilt_up', label: 'Tilt up' },
    { value: 'tilt_down', label: 'Tilt down' },
  ],
  seedance: [
    { value: 'auto', label: 'Auto' },
    { value: 'static', label: 'Static' },
    { value: 'dolly_in', label: 'Dolly in' },
    { value: 'dolly_out', label: 'Dolly out' },
    { value: 'pan_left', label: 'Pan left' },
    { value: 'pan_right', label: 'Pan right' },
    { value: 'tilt_up', label: 'Tilt up' },
    { value: 'tilt_down', label: 'Tilt down' },
  ],
};

const CAMERA_DIRECTION_PRESETS = {
  nano_banana: [
    { value: '', label: 'Custom' },
    { value: '[Static shot]', label: 'Static shot' },
    { value: '[Pan left]', label: 'Pan left' },
    { value: '[Pan right]', label: 'Pan right' },
    { value: '[Tilt up]', label: 'Tilt up' },
    { value: '[Tilt down]', label: 'Tilt down' },
    { value: '[Zoom in]', label: 'Zoom in' },
    { value: '[Zoom out]', label: 'Zoom out' },
  ],
  minimax: [
    { value: '', label: 'Custom' },
    { value: '[Static shot]', label: 'Static shot' },
    { value: '[Pan left]', label: 'Pan left' },
    { value: '[Pan right]', label: 'Pan right' },
    { value: '[Push in]', label: 'Push in' },
    { value: '[Pull out]', label: 'Pull out' },
    { value: 'low-angle shot, 35mm lens', label: 'Low-angle (35mm lens)' },
    { value: 'high-angle shot, 50mm lens', label: 'High-angle (50mm lens)' },
  ],
  veo3: [
    { value: '', label: 'Custom' },
    { value: '[Static shot]', label: 'Static shot' },
    { value: '[Tracking shot]', label: 'Tracking shot' },
    { value: '[Pan left]', label: 'Pan left' },
    { value: '[Pan right]', label: 'Pan right' },
    { value: '[Tilt up]', label: 'Tilt up' },
    { value: '[Tilt down]', label: 'Tilt down' },
    { value: '[Push in]', label: 'Push in' },
    { value: '[Pull out]', label: 'Pull out' },
    { value: '[Zoom in]', label: 'Zoom in' },
    { value: '[Zoom out]', label: 'Zoom out' },
  ],
  runway: [
    { value: '', label: 'Custom' },
    { value: '[Static shot]', label: 'Static shot' },
    { value: '[Tracking shot]', label: 'Tracking shot' },
    { value: '[Truck left]', label: 'Truck left' },
    { value: '[Truck right]', label: 'Truck right' },
    { value: '[Pan left]', label: 'Pan left' },
    { value: '[Pan right]', label: 'Pan right' },
    { value: '[Push in]', label: 'Push in' },
    { value: '[Pull out]', label: 'Pull out' },
    { value: '[Pedestal up]', label: 'Pedestal up' },
    { value: '[Pedestal down]', label: 'Pedestal down' },
    { value: '[Tilt up]', label: 'Tilt up' },
    { value: '[Tilt down]', label: 'Tilt down' },
    { value: '[Zoom in]', label: 'Zoom in' },
    { value: '[Zoom out]', label: 'Zoom out' },
    { value: '[Shake]', label: 'Shake' },
    { value: 'low-angle shot, 35mm lens', label: 'Low-angle (35mm lens)' },
    { value: 'high-angle shot, 50mm lens', label: 'High-angle (50mm lens)' },
  ],
  seedance: [
    { value: '', label: 'Custom' },
    { value: '[Static shot]', label: 'Static shot' },
    { value: '[Tracking shot]', label: 'Tracking shot' },
    { value: '[Truck left]', label: 'Truck left' },
    { value: '[Truck right]', label: 'Truck right' },
    { value: '[Pan left]', label: 'Pan left' },
    { value: '[Pan right]', label: 'Pan right' },
    { value: '[Push in]', label: 'Push in' },
    { value: '[Pull out]', label: 'Pull out' },
    { value: '[Pedestal up]', label: 'Pedestal up' },
    { value: '[Pedestal down]', label: 'Pedestal down' },
    { value: '[Tilt up]', label: 'Tilt up' },
    { value: '[Tilt down]', label: 'Tilt down' },
    { value: '[Zoom in]', label: 'Zoom in' },
    { value: '[Zoom out]', label: 'Zoom out' },
    { value: '[Shake]', label: 'Shake' },
    { value: 'low-angle shot, 35mm lens', label: 'Low-angle (35mm lens)' },
    { value: 'high-angle shot, 50mm lens', label: 'High-angle (50mm lens)' },
  ],
};

const supportedVideoEngines = [
  'inhouse',
  'nano_banana',
  'minimax',
  'veo3',
  'runway',
  'seedance',
  'seedance_2_5',
  'chatgpt_astra',
  'fable_5_1',
];
const runwayAudioModels = [
  { value: 'eleven_multilingual_v2', label: 'eleven_multilingual_v2' },
  { value: 'eleven_text_to_sound_v2', label: 'eleven_text_to_sound_v2' },
  { value: 'eleven_voice_isolation', label: 'eleven_voice_isolation' },
  { value: 'eleven_voice_dubbing', label: 'eleven_voice_dubbing' },
  { value: 'eleven_multilingual_sts_v2', label: 'eleven_multilingual_sts_v2' },
];
const runwayDubLanguages = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'ja', label: 'Japanese' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ar', label: 'Arabic' },
  { value: 'ru', label: 'Russian' },
  { value: 'ko', label: 'Korean' },
  { value: 'it', label: 'Italian' },
  { value: 'nl', label: 'Dutch' },
  { value: 'tr', label: 'Turkish' },
  { value: 'pl', label: 'Polish' },
  { value: 'sv', label: 'Swedish' },
  { value: 'id', label: 'Indonesian' },
  { value: 'fil', label: 'Filipino' },
  { value: 'ms', label: 'Malay' },
  { value: 'ta', label: 'Tamil' },
];

function normalizeVideoEngine(engine) {
  return supportedVideoEngines.includes(engine) ? engine : 'nano_banana';
}

const NARRATION_IMAGE_PROVIDER_OPTIONS = [
  { value: 'nano_banana', label: 'Gemini Nano Banana' },
  { value: 'veo3', label: 'Veo 3 / Gemini Images' },
  { value: 'minimax', label: 'MiniMax Hailuo' },
  { value: 'runway', label: 'Runway Gen4' },
  { value: 'inhouse', label: 'Inhouse' },
];

const NARRATION_VIDEO_PROVIDER_OPTIONS = [
  { value: 'narration_slideshow', label: 'Narration slideshow' },
  { value: 'minimax', label: 'MiniMax Hailuo AI Video' },
  { value: 'veo3', label: 'Google Veo' },
  { value: 'runway', label: 'Runway ML' },
  { value: 'seedance', label: 'Seedance 2 (Runway)' },
  { value: 'seedance_2_5', label: 'Seedance 2.5 (Runway)' },
  { value: 'chatgpt_astra', label: 'ChatGPT Astra (OpenAI Video)' },
  { value: 'fable_5_1', label: 'Fable 5.1 (via Seedance 2.5)' },
  { value: 'inhouse', label: 'Inhouse' },
];

const STORY_VIDEO_PROVIDER_OPTIONS = [
  { value: 'nano_banana', label: 'Nano Banana slideshow' },
  { value: 'minimax', label: 'MiniMax Hailuo AI Video' },
  { value: 'veo3', label: 'Google Veo' },
  { value: 'runway', label: 'Runway ML' },
  { value: 'seedance', label: 'Seedance 2 (Runway)' },
  { value: 'seedance_2_5', label: 'Seedance 2.5 (Runway)' },
  { value: 'chatgpt_astra', label: 'ChatGPT Astra (OpenAI Video)' },
  { value: 'fable_5_1', label: 'Fable 5.1 (via Seedance 2.5)' },
  { value: 'inhouse', label: 'Inhouse' },
];

function imageProviderLabel(provider) {
  const match = NARRATION_IMAGE_PROVIDER_OPTIONS.find((o) => o.value === provider);
  if (match) return match.label;
  const p = (provider || '').toLowerCase();
  if (p === 'runway') return 'Runway';
  if (p === 'minimax') return 'MiniMax';
  if (p === 'openai') return 'OpenAI';
  if (p === 'inhouse' || p === 'bedrock') return 'Inhouse';
  if (p === 'gemini' || p === 'veo3') return 'Gemini';
  return 'Nano Banana';
}

function videoProviderLabel(provider) {
  const options = [...NARRATION_VIDEO_PROVIDER_OPTIONS, ...STORY_VIDEO_PROVIDER_OPTIONS];
  const match = options.find((o) => o.value === provider);
  return match?.label || provider || 'Video';
}

function sceneHasImageGenerationPrompt(scene) {
  return Boolean(
    String(scene?.visual || scene?.camera_direction || scene?.scene_text || scene?.text || '').trim(),
  );
}

// Concurrent POST burst to enqueue-image endpoint. Each returns job_id immediately;
// real generation concurrency is controlled server-side by GEMINI_DEFAULT_CONCURRENCY_IMAGE
// (and equivalent per-provider env vars) on the Celery provider queues.
const NARRATION_IMAGE_BATCH_CONCURRENCY = 16;

async function mapWithConcurrency(items, limit, iterator) {
  if (!items.length) return [];
  const results = new Array(items.length);
  let nextIndex = 0;
  const workerCount = Math.min(Math.max(1, limit), items.length);

  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await iterator(items[index], index);
    }
  };

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

function sceneScriptTextForAdapt(scene) {
  const direct = (scene?.scene_text || scene?.narration_text || scene?.text || '').trim();
  if (direct) return direct;
  const tl = scene?.audio_timeline;
  if (!Array.isArray(tl)) return '';
  return tl.map((item) => String(item?.text || '').trim()).filter(Boolean).join(' ').trim();
}

function sceneHasVideoForAdapt(scene) {
  if (String(scene?.video_url || '').trim()) return true;
  const hist = scene?.video_history;
  if (!Array.isArray(hist)) return false;
  return hist.some((entry) => {
    const url = typeof entry === 'string' ? entry : entry?.url;
    return String(url || '').trim();
  });
}

export const SceneGridEditor = ({ editorMode: editorModeProp } = {}) => {
  const { storyId, projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { openInsufficientCredits } = useInsufficientCredits();
  const isNarrationMode = editorModeProp === 'narration'
    || Boolean(projectId && (location.pathname || '').includes('/narration/') && !storyId);
  const editorEntityId = isNarrationMode ? projectId : storyId;
  // Demo mode: bypass all backend calls so the editor UI can be previewed freely
  const isDemo = projectId === 'demo-preview';
  const demoGuard = () => {
    if (isDemo) { toast.info('Demo mode — backend actions are disabled for UI preview.'); return true; }
    return false;
  };
  const { open: movieDirectorOpen, setOpen: setMovieDirectorOpen } = useMovieDirector();
  const storySound = useStorySoundGate(isNarrationMode ? '' : storyId);
  const [soundPanelOpen, setSoundPanelOpen] = useState(false);
  const [story, setStory] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scenesLoading, setScenesLoading] = useState(false);
  const [scenesLoadingMore, setScenesLoadingMore] = useState(false);
  const [scenesTotal, setScenesTotal] = useState(0);
  const [scenesHasMore, setScenesHasMore] = useState(false);
  const [scenesNextOffset, setScenesNextOffset] = useState(0);
  const loadMoreSentinelRef = useRef(null);
  const scenesPageLockRef = useRef(false);
  const narrationProjectRef = useRef(null);
  const [editingScene, setEditingScene] = useState(null);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [generatingImages, setGeneratingImages] = useState({});
  const [generatingVideos, setGeneratingVideos] = useState({});
  const [savingCameraDirection, setSavingCameraDirection] = useState({});
  const [savingImagePrompt, setSavingImagePrompt] = useState({});
  const [lipSyncingVideos, setLipSyncingVideos] = useState({});
  const [expandingImages, setExpandingImages] = useState({});
  const [generatingSceneSfx, setGeneratingSceneSfx] = useState({});
  const [generatingStorySfx, setGeneratingStorySfx] = useState(false);
  const [exportingStory, setExportingStory] = useState(false);
  const [exportedVideoUrl, setExportedVideoUrl] = useState(null);
  const [exportingPremiere, setExportingPremiere] = useState(false);
  const [doodleModalScene, setDoodleModalScene] = useState(null);
  const [storyAudioVoice, setStoryAudioVoice] = useState('storyteller');
  const [storyAudioLanguage, setStoryAudioLanguage] = useState('en');
  const [storyTtsProvider, setStoryTtsProvider] = useState('auto');
  const [storyNarratorMinimax, setStoryNarratorMinimax] = useState('');
  const [storyNarratorEleven, setStoryNarratorEleven] = useState('');
  const [storyNarratorMurf, setStoryNarratorMurf] = useState('');
  const [storyNarratorMurfLocale, setStoryNarratorMurfLocale] = useState('');
  const [murfVoicesCatalog, setMurfVoicesCatalog] = useState([]);
  const [narrationVoicePickerOpen, setNarrationVoicePickerOpen] = useState(false);
  const persistAudioSettingsTimerRef = useRef(null);
  const [savePerLineAudioClips, setSavePerLineAudioClips] = useState(true);
  const [storyAudioGenerating, setStoryAudioGenerating] = useState(false);
  const [storyAudioDubGenerating, setStoryAudioDubGenerating] = useState(false);
  const [dubTargetLanguage, setDubTargetLanguage] = useState('en');
  const [dubDisableVoiceCloning, setDubDisableVoiceCloning] = useState(true);
  const [dubDropBackgroundAudio, setDubDropBackgroundAudio] = useState(false);
  const [storyAudioPanelOpen, setStoryAudioPanelOpen] = useState(false);
  const [sceneAudioTarget, setSceneAudioTarget] = useState(null);
  const [dialogueCameraSceneId, setDialogueCameraSceneId] = useState(null);
  const [sceneAudioGeneratingId, setSceneAudioGeneratingId] = useState(null);
  const [modalAudioLanguage, setModalAudioLanguage] = useState('en');
  const [modalAudioVoice, setModalAudioVoice] = useState('storyteller');
  const [modalNarratorMinimax, setModalNarratorMinimax] = useState('');
  const [modalNarratorMurf, setModalNarratorMurf] = useState('');
  const [modalNarratorMurfLocale, setModalNarratorMurfLocale] = useState('');
  const [modalNarratorEleven, setModalNarratorEleven] = useState('');
  const [modalNarratorTtsProvider, setModalNarratorTtsProvider] = useState('auto');
  const [modalNarratorRunwayModel, setModalNarratorRunwayModel] = useState('eleven_multilingual_v2');
  const [modalDialogueTtsDefault, setModalDialogueTtsDefault] = useState('auto');
  const [modalCharMinimax, setModalCharMinimax] = useState({});
  const [modalCharMurf, setModalCharMurf] = useState({});
  const [modalCharMurfLocale, setModalCharMurfLocale] = useState({});
  const [modalCharEleven, setModalCharEleven] = useState({});
  const [modalCharDialogueProvider, setModalCharDialogueProvider] = useState({});
  const [modalCharRunwayModel, setModalCharRunwayModel] = useState({});
  const [sceneScriptDraft, setSceneScriptDraft] = useState([]);
  const [addSceneOpen, setAddSceneOpen] = useState(false);
  const [addScenePlaceAs, setAddScenePlaceAs] = useState(1);
  const [addSceneTitle, setAddSceneTitle] = useState('');
  const [addSceneText, setAddSceneText] = useState('');
  const [addSceneSaving, setAddSceneSaving] = useState(false);
  const [extendStoryOpen, setExtendStoryOpen] = useState(false);
  const [extendScriptSource, setExtendScriptSource] = useState('ai');
  const [extendStoryIdea, setExtendStoryIdea] = useState('');
  const [extendStartFromScene, setExtendStartFromScene] = useState(1);
  const [extendLengthMode, setExtendLengthMode] = useState('minutes');
  const [extensionMinutes, setExtensionMinutes] = useState(3);
  const [extensionSceneCount, setExtensionSceneCount] = useState(6);
  const [extendOptions, setExtendOptions] = useState([]);
  const [selectedExtendOptionId, setSelectedExtendOptionId] = useState('');
  const [extendProposing, setExtendProposing] = useState(false);
  const [extendCommitting, setExtendCommitting] = useState(false);
  const [extendCommitMessage, setExtendCommitMessage] = useState('');
  const [extendReferenceVisualStyle, setExtendReferenceVisualStyle] = useState('');
  const [extendStyleProfiles, setExtendStyleProfiles] = useState([]);
  const [bulkAudioDialogOpen, setBulkAudioDialogOpen] = useState(false);
  const [bulkAudioGenerating, setBulkAudioGenerating] = useState(false);
  const [bulkAudioProgress, setBulkAudioProgress] = useState({ current: 0, total: 0, name: '' });
  const [narrationImageProvider, setNarrationImageProvider] = useState('nano_banana');
  const [bulkImageDialogOpen, setBulkImageDialogOpen] = useState(false);
  const [bulkImageGenerating, setBulkImageGenerating] = useState(false);
  const [bulkImageProgress, setBulkImageProgress] = useState({ current: 0, total: 0, name: '' });
  const [bulkVideoDialogOpen, setBulkVideoDialogOpen] = useState(false);
  const [bulkVideoGenerating, setBulkVideoGenerating] = useState(false);
  const [bulkVideoProgress, setBulkVideoProgress] = useState({ current: 0, total: 0, name: '' });
  const [narrationVideoProvider, setNarrationVideoProvider] = useState('narration_slideshow');
  const [batchVideoProvider, setBatchVideoProvider] = useState('nano_banana');
  const [singleGenDialog, setSingleGenDialog] = useState(null);
  const [singleGenProvider, setSingleGenProvider] = useState('nano_banana');
  const [videoPromptReview, setVideoPromptReview] = useState(null);
  const [videoPromptReviewLoading, setVideoPromptReviewLoading] = useState(false);
  const [videoCreditEstimate, setVideoCreditEstimate] = useState(null);
  const [bulkAdaptScriptDialogOpen, setBulkAdaptScriptDialogOpen] = useState(false);
  const [bulkAdaptScriptGenerating, setBulkAdaptScriptGenerating] = useState(false);
  const [bulkAdaptScriptProgress, setBulkAdaptScriptProgress] = useState({ current: 0, total: 0, name: '' });
  const [adaptScriptRegenerateAudio, setAdaptScriptRegenerateAudio] = useState(false);
  const [adaptingScriptSceneId, setAdaptingScriptSceneId] = useState(null);
  const [selectedSceneIds, setSelectedSceneIds] = useState(() => new Set());
  const [deletingScenes, setDeletingScenes] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [songAnimationEnabled, setSongAnimationEnabled] = useState(true);
  const [songAnimationSpeed, setSongAnimationSpeed] = useState(SONG_ANIMATION_SPEED_DEFAULT);
  const [bulkMultishotDialogOpen, setBulkMultishotDialogOpen] = useState(false);
  const [multishotCount, setMultishotCount] = useState(2);
  const [multishotPromptTemplate, setMultishotPromptTemplate] = useState(DEFAULT_MULTISHOT_PROMPT_TEMPLATE);
  const [bulkMultishotGenerating, setBulkMultishotGenerating] = useState(false);
  const [multishotSelectionSavingId, setMultishotSelectionSavingId] = useState(null);
  const multishotPollRef = useRef(null);
  const persistSongAnimationTimerRef = useRef(null);

  const characterById = useMemo(() => {
    const m = {};
    for (const c of story?.characters || []) {
      if (c?.id) m[c.id] = c.name || c.id;
    }
    return m;
  }, [story?.characters]);

  const isDialogueStory = !isNarrationMode && String(story?.story_type || '').toLowerCase() === 'dialogue_story';
  const dialogueCameraScene = dialogueCameraSceneId
    ? scenes.find((s) => s.id === dialogueCameraSceneId) || null
    : null;
  const patchDialogueCameraScene = useCallback((fields) => {
    if (!dialogueCameraSceneId) return;
    setScenes((prev) => prev.map((s) => (s.id === dialogueCameraSceneId ? { ...s, ...fields } : s)));
  }, [dialogueCameraSceneId]);
  const patchDialogueCameraStory = useCallback((fields) => {
    setStory((prev) => (prev ? { ...prev, ...fields } : prev));
  }, []);

  const segmentApiPath = useCallback(
    (segmentId, suffix = '') => (
      isNarrationMode
        ? `/narration/projects/${projectId}/segments/${segmentId}${suffix}`
        : `/scenes/${segmentId}${suffix}`
    ),
    [isNarrationMode, projectId],
  );

  const narrationAudioSettings = useMemo(
    () => ({
      voice_style: storyAudioVoice,
      tts_provider: storyTtsProvider,
      language: storyAudioLanguage,
      narrator_minimax_voice_id: storyNarratorMinimax,
      narrator_elevenlabs_voice_id: storyNarratorEleven,
      narrator_murf_voice_id: storyNarratorMurf,
      narrator_murf_locale: storyNarratorMurfLocale,
    }),
    [
      storyAudioVoice,
      storyTtsProvider,
      storyAudioLanguage,
      storyNarratorMinimax,
      storyNarratorEleven,
      storyNarratorMurf,
      storyNarratorMurfLocale,
    ],
  );

  const applyNarrationAudioSettings = useCallback((next) => {
    const s = narrationAudioSettingsFromInput(next);
    setStoryAudioVoice(s.voice_style);
    setStoryTtsProvider(s.tts_provider);
    setStoryAudioLanguage(s.language);
    setStoryNarratorMinimax(s.narrator_minimax_voice_id);
    setStoryNarratorEleven(s.narrator_elevenlabs_voice_id);
    setStoryNarratorMurf(s.narrator_murf_voice_id);
    setStoryNarratorMurfLocale(s.narrator_murf_locale);
  }, []);

  const persistNarrationAudioSettings = useCallback(
    (settings) => {
      if (!isNarrationMode || !projectId || isDemo) return;
      if (persistAudioSettingsTimerRef.current) {
        clearTimeout(persistAudioSettingsTimerRef.current);
      }
      persistAudioSettingsTimerRef.current = setTimeout(async () => {
        try {
          await api.patch(`/narration/projects/${projectId}/audio-settings`, settings);
        } catch {
          /* non-blocking */
        }
      }, 500);
    },
    [isNarrationMode, projectId],
  );

  const onNarrationAudioSettingsChange = useCallback(
    (next) => {
      applyNarrationAudioSettings(next);
      persistNarrationAudioSettings(narrationAudioSettingsFromInput(next));
    },
    [applyNarrationAudioSettings, persistNarrationAudioSettings],
  );

  const buildNarrationAudioPayload = useCallback(
    (extra = {}) => buildNarrationAudioApiPayload(narrationAudioSettings, extra),
    [narrationAudioSettings],
  );

  const hydrateNarrationAudioFromProject = useCallback((projectData) => {
    const audio = narrationAudioSettingsFromInput(projectData?.input);
    applyNarrationAudioSettings(audio);
  }, [applyNarrationAudioSettings]);

  useEffect(() => {
    if (storyTtsProvider !== 'murf') return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await fetchMurfVoiceCatalog(api);
        if (!cancelled) setMurfVoicesCatalog(data?.voices || []);
      } catch {
        if (!cancelled) setMurfVoicesCatalog([]);
      }
    })();
    return () => { cancelled = true; };
  }, [storyTtsProvider]);

  const selectNarrationVoice = useCallback(
    (voice) => {
      const prov = storyTtsProvider;
      const patch = { ...narrationAudioSettings };
      if (prov === 'elevenlabs') patch.narrator_elevenlabs_voice_id = voice.voice_id;
      if (prov === 'murf') {
        patch.narrator_murf_voice_id = voice.voice_id;
        patch.narrator_murf_locale = voice.locale || patch.narrator_murf_locale;
        patch.voice_style = normalizeVoiceStyleForProvider('murf', patch.voice_style, {
          murfVoices: murfVoicesCatalog,
          murfVoiceId: voice.voice_id,
          murfLocale: voice.locale || patch.narrator_murf_locale,
          language: patch.language,
        });
      }
      if (prov === 'minimax') patch.narrator_minimax_voice_id = voice.voice_id;
      onNarrationAudioSettingsChange(patch);
      setNarrationVoicePickerOpen(false);
      toast.success('Narration voice updated');
    },
    [storyTtsProvider, narrationAudioSettings, onNarrationAudioSettingsChange, murfVoicesCatalog],
  );

  const applyNarrationSegmentUpdate = useCallback((segment) => {
    if (!isNarrationMode || !String(segment?.id || '').trim()) return;
    const project = {
      id: projectId,
      title: story?.title,
      app: story?.app,
      input: story?.input || {
        topic: story?.user_topic,
        visual_style: story?.visual_style,
        language: story?.language,
        image_provider: story?.image_provider,
        project_kind: story?.project_kind,
      },
    };
    setScenes((prev) => mergeNarrationSegmentIntoScenes(prev, segment, project));
  }, [isNarrationMode, projectId, story]);

  const dispatchMultishotGenerated = useCallback((segmentId) => {
    if (!projectId) return;
    window.dispatchEvent(new CustomEvent('twin-tales-multishot-generated', {
      detail: { projectId, segmentId },
    }));
  }, [projectId]);

  const pollMultishotJobProgress = useCallback(async (jobId, targetScenes) => {
    if (!projectId || !jobId || !targetScenes?.length) return;
    if (multishotPollRef.current) {
      clearInterval(multishotPollRef.current);
      multishotPollRef.current = null;
    }
    const targetIds = targetScenes.map((scene) => scene.id);
    const prevCounts = Object.fromEntries(
      targetScenes.map((scene) => [scene.id, (scene.multishot_images || []).length]),
    );

    const poll = async () => {
      try {
        // Lean paginated scenes — never the fat GET /projects/{id} (timeouts on large stories).
        const [{ data: job }, page] = await Promise.all([
          api.get(`/jobs/${jobId}`),
          fetchNarrationSceneEditScenesPage(projectId, {
            limit: 60,
            offset: 0,
          }),
        ]);
        let segments = page.segments || [];
        if (page.has_more) {
          const more = await fetchNarrationSceneEditScenesPage(projectId, {
            limit: 60,
            offset: page.segments.length,
          });
          segments = segments.concat(more.segments || []);
        }
        segments.forEach((seg) => {
          if (!targetIds.includes(seg.id)) return;
          const count = (seg.multishot_images || []).length;
          if (count > (prevCounts[seg.id] || 0)) {
            applyNarrationSegmentUpdate(seg);
            prevCounts[seg.id] = count;
            dispatchMultishotGenerated(seg.id);
          }
        });
        if (job?.status === 'completed' || job?.status === 'failed') {
          if (multishotPollRef.current) {
            clearInterval(multishotPollRef.current);
            multishotPollRef.current = null;
          }
        }
      } catch {
        /* ignore transient poll errors */
      }
    };

    await poll();
    multishotPollRef.current = setInterval(poll, 4000);
  }, [applyNarrationSegmentUpdate, dispatchMultishotGenerated, projectId]);

  useEffect(() => () => {
    if (multishotPollRef.current) clearInterval(multishotPollRef.current);
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorEntityId, isNarrationMode]);

  useEffect(() => {
    if (isNarrationMode && story?.image_provider) {
      setNarrationImageProvider(story.image_provider);
    }
  }, [isNarrationMode, story?.image_provider]);

  useEffect(() => {
    if (isNarrationMode && story?._narrationVideoProvider) {
      setNarrationVideoProvider(story._narrationVideoProvider);
    }
  }, [isNarrationMode, story?._narrationVideoProvider]);

  useEffect(() => {
    if (isNarrationMode) return;
    const dp = story?.default_scene_video_provider;
    if (dp && STORY_VIDEO_PROVIDER_OPTIONS.some((o) => o.value === dp)) {
      setBatchVideoProvider(dp);
    }
  }, [isNarrationMode, story?.default_scene_video_provider]);

  useEffect(() => {
    if (!story?.characters?.length) return;
    const initMm = {};
    const initMf = {};
    const initMloc = {};
    const initEl = {};
    const initDp = {};
    const initRw = {};
    for (const c of story.characters) {
      if (!c?.id) continue;
      initMm[c.id] = c.minimax_voice_id || '';
      initMf[c.id] = c.murf_voice_id || '';
      initMloc[c.id] = c.murf_locale || '';
      initEl[c.id] = c.elevenlabs_voice_id || '';
      initDp[c.id] = c.dialogue_tts_provider || 'auto';
      initRw[c.id] = c.runway_model || 'eleven_multilingual_v2';
    }
    setModalCharMinimax((prev) => ({ ...initMm, ...prev }));
    setModalCharMurf((prev) => ({ ...initMf, ...prev }));
    setModalCharMurfLocale((prev) => ({ ...initMloc, ...prev }));
    setModalCharEleven((prev) => ({ ...initEl, ...prev }));
    setModalCharDialogueProvider((prev) => ({ ...initDp, ...prev }));
    setModalCharRunwayModel((prev) => ({ ...initRw, ...prev }));
  }, [story?.characters]);

  useEffect(() => {
    if (!sceneAudioTarget?.id) return;
    setModalAudioLanguage(storyAudioLanguage);
    setModalAudioVoice(storyAudioVoice);
    setModalNarratorMinimax(storyNarratorMinimax);
    setModalNarratorRunwayModel('eleven_multilingual_v2');
    if (isNarrationMode) {
      setSceneScriptDraft([
        {
          kind: 'narration',
          text: sceneAudioTarget.scene_text || sceneAudioTarget.text || '',
          character_id: '',
          speaker: '',
        },
      ]);
      return;
    }
    const normalized = scriptToDraft(sceneAudioTarget.audio_script);
    setSceneScriptDraft(
      normalized.length
        ? normalized
        : [{ kind: 'dialogue', text: '', character_id: '', speaker: '' }],
    );
  }, [sceneAudioTarget?.id, storyAudioLanguage, storyAudioVoice, storyNarratorMinimax, isNarrationMode]);

  const loadData = async () => {
    try {
      if (isNarrationMode) {
        if (!projectId) {
          navigate('/dashboard');
          return;
        }

        // ── Demo bypass: open editor UI without a real project ──
        if (projectId === 'demo-preview') {
          const demoProject = {
            id: 'demo-preview',
            title: 'Demo Narration Project',
            status: 'completed',
            image_provider: 'nano_banana',
            project_kind: 'narration',
            input: { topic: 'A demo story', visual_style: 'anime', language: 'en' },
            segments: [
              {
                id: 'seg-1', segment_number: 1, text: 'Scene 1 — The adventure begins in a mysterious forest.', visual: 'A lush animated forest at dawn, glowing fireflies.', image_url: null, video_url: null, audio_url: null,
              },
              {
                id: 'seg-2', segment_number: 2, text: 'Scene 2 — Our hero discovers a hidden map.', visual: 'Close-up of ancient parchment map on a wooden table.', image_url: null, video_url: null, audio_url: null,
              },
              {
                id: 'seg-3', segment_number: 3, text: 'Scene 3 — The journey to the mountain peak begins.', visual: 'Wide shot of snow-capped mountains under a dramatic sky.', image_url: null, video_url: null, audio_url: null,
              },
              {
                id: 'seg-4', segment_number: 4, text: 'Scene 4 — A twist: the treasure was friendship all along.', visual: 'Warm campfire scene with silhouettes of friends gathered.', image_url: null, video_url: null, audio_url: null,
              },
            ],
          };
          hydrateNarrationAudioFromProject(demoProject);
          setStory(narrationProjectToStory(demoProject));
          setScenes(narrationSegmentsFromProject(demoProject));
          setLoading(false);
          return;
        }
        // ── End demo bypass ──

        // Shell + first page in parallel; skeletons until scenes land.
        setScenesLoading(true);
        setScenesHasMore(false);
        setScenesNextOffset(0);
        try {
          const [shell, page] = await Promise.all([
            fetchNarrationSceneEditShell(projectId),
            fetchNarrationSceneEditScenesPage(projectId, {
              limit: SCENE_EDIT_PAGE_SIZE,
              offset: 0,
            }),
          ]);
          narrationProjectRef.current = shell;
          hydrateNarrationAudioFromProject(shell);
          if (isSongBasedNarrationProject(shell)) {
            applySongAnimationSettings(songAnimationSettingsFromInput(shell?.input));
          }
          setStory(narrationProjectToStory(shell));
          const mapped = mapEditSegmentsToScenes(page.segments, shell);
          setScenes(mapped);
          if (page.total != null) setScenesTotal(page.total);
          setScenesHasMore(page.has_more);
          setScenesNextOffset(page.offset + page.segments.length);
        } finally {
          setScenesLoading(false);
        }
        setLoading(false);
        return;
      } else {
        const { data: storyData } = await api.get(`/stories/${storyId}`);
        setStory(storyData);
        if (storyData?.language) setStoryAudioLanguage(storyData.language);
        const { data: scenesData } = await api.get(`/stories/${storyId}/scenes`, {
          params: { _fresh: Date.now() },
        });
        setScenes(scenesData);
      }
    } catch (err) {
      toast.error(
        formatApiError(
          err,
          isNarrationMode ? 'Failed to load narration project' : 'Failed to load story',
        ),
      );
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreNarrationScenes = useCallback(async () => {
    if (!isNarrationMode || !projectId) return;
    if (!scenesHasMore || scenesLoadingMore || scenesPageLockRef.current) return;
    scenesPageLockRef.current = true;
    setScenesLoadingMore(true);
    try {
      const project = narrationProjectRef.current || { id: projectId, input: story?.input || {} };
      const page = await fetchNarrationSceneEditScenesPage(projectId, {
        limit: SCENE_EDIT_PAGE_SIZE,
        offset: scenesNextOffset,
      });
      const mapped = mapEditSegmentsToScenes(page.segments, project);
      setScenes((prev) => mergeSceneEditPages(prev, mapped));
      if (page.total != null) setScenesTotal(page.total);
      setScenesHasMore(page.has_more);
      setScenesNextOffset(page.offset + page.segments.length);
    } catch (err) {
      console.warn('Scene Edit page load failed', err);
    } finally {
      setScenesLoadingMore(false);
      scenesPageLockRef.current = false;
    }
  }, [
    isNarrationMode,
    projectId,
    scenesHasMore,
    scenesLoadingMore,
    scenesNextOffset,
    story?.input,
  ]);

  useEffect(() => {
    if (!isNarrationMode || !scenesHasMore) return undefined;
    const el = loadMoreSentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        loadMoreNarrationScenes();
      }
    }, { rootMargin: '200px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [isNarrationMode, scenesHasMore, loadMoreNarrationScenes, scenes.length]);


  const handleDoodleImageGenerated = async (imageUrl) => {
    // Reload scenes to get the updated image
    await loadData();
  };

  const openAddSceneDialog = () => {
    const n = scenes.length;
    setAddScenePlaceAs(Math.max(1, n + 1));
    setAddSceneTitle('');
    setAddSceneText('');
    setAddSceneOpen(true);
  };

  const handleAddSceneSave = async () => {
    const n = scenes.length;
    const placeAs = Math.min(Math.max(1, Number(addScenePlaceAs) || 1), n + 1);
    setAddSceneSaving(true);
    try {
      const { data } = await api.post(`/stories/${storyId}/scenes`, {
        insert_at: placeAs - 1,
        scene_title: addSceneTitle.trim() || undefined,
        scene_text: addSceneText.trim() || undefined,
      });
      setScenes(Array.isArray(data) ? data : []);
      toast.success('Scene added and order saved');
      setAddSceneOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add scene');
    } finally {
      setAddSceneSaving(false);
    }
  };

  const updateSceneText = async (sceneId, newText) => {
    if (demoGuard()) return;
    try {
      const scene = scenes.find((s) => s.id === sceneId);
      if (isNarrationMode) {
        const { data } = await api.patch(segmentApiPath(sceneId), {
          text: newText,
          visual: scene?.visual || newText,
          emotion: scene?.emotion,
          phoneme_notes: scene?.phoneme_notes || '',
        });
        if (data?.id) {
          applyNarrationSegmentUpdate(data);
        }
        else {
          setScenes((prev) =>
            prev.map((s) => (s.id === sceneId ? { ...s, scene_text: newText, text: newText } : s)),
          );
        }
      } else {
        await api.put(`/scenes/${sceneId}`, { scene_text: newText });
        setScenes((prev) =>
          prev.map((s) => (s.id === sceneId ? { ...s, scene_text: newText, text: newText } : s)),
        );
      }
      setEditingScene(null);
      toast.success('Scene updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const selectedExtendOption = useMemo(
    () => extendOptions.find((o) => o.id === selectedExtendOptionId) || null,
    [extendOptions, selectedExtendOptionId],
  );

  const lastSceneNumber = useMemo(() => {
    if (!scenes.length) return 1;
    return Math.max(
      ...scenes.map((s, idx) => Number(s.segment_number || s.scene_number) || idx + 1),
    );
  }, [scenes]);

  const openExtendStoryDialog = () => {
    setExtendStoryOpen(true);
    setExtendOptions([]);
    setSelectedExtendOptionId('');
    setExtendScriptSource('ai');
    setExtendStoryIdea('');
    setExtendStartFromScene(lastSceneNumber);
    setExtendLengthMode('minutes');
    setExtensionMinutes(3);
    setExtensionSceneCount(6);
  };

  const proposeStoryExtension = async () => {
    if (!projectId) {
      toast.error('Narration project not loaded yet');
      return;
    }
    const startFrom = Math.min(
      lastSceneNumber,
      Math.max(1, Number(extendStartFromScene) || lastSceneNumber),
    );
    const payload = { start_from_segment_number: startFrom };
    const isManual = extendScriptSource === 'manual';
    if (extendLengthMode === 'scenes') {
      payload.extension_scene_count = Math.min(28, Math.max(3, Number(extensionSceneCount) || 6));
    } else {
      payload.extension_minutes = Math.min(30, Math.max(1, Number(extensionMinutes) || 3));
    }
    if (isManual) {
      const idea = extendStoryIdea.trim();
      if (idea.length < 12) {
        toast.error('Describe your extension story idea in a few sentences');
        return;
      }
      payload.manual_script = idea;
    }
    setExtendProposing(true);
    setExtendOptions([]);
    setSelectedExtendOptionId('');
    setExtendCommitMessage('');
    setExtendReferenceVisualStyle('');
    setExtendStyleProfiles([]);
    try {
      const { data } = await api.post(`/narration/projects/${projectId}/extend-story/propose`, payload);
      const opts = Array.isArray(data?.options) ? data.options : [];
      setExtendOptions(opts);
      setExtendReferenceVisualStyle(String(data?.reference_visual_style || opts[0]?.reference_visual_style || ''));
      setExtendStyleProfiles(Array.isArray(data?.style_sample_profiles) ? data.style_sample_profiles : (opts[0]?.style_sample_profiles || []));
      if (opts.length) setSelectedExtendOptionId(opts[0].id);
      toast.success(
        isManual
          ? `Generated ${opts[0]?.segments?.length || 0} scene(s) with image prompts from your idea`
          : `Generated ${opts.length} continuation option(s)`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate extension options');
    } finally {
      setExtendProposing(false);
    }
  };

  const updateExtendOptionSegment = useCallback((optionId, segIdx, field, value) => {
    setExtendOptions((prev) => prev.map((opt) => {
      if (opt.id !== optionId) return opt;
      const segments = Array.isArray(opt.segments) ? [...opt.segments] : [];
      const current = segments[segIdx] && typeof segments[segIdx] === 'object'
        ? { ...segments[segIdx] }
        : {};
      const next = { ...current, [field]: value };
      if (field === 'text') {
        next.scene_text = value;
        next.narration_text = value;
      }
      if (field === 'visual') {
        next.camera_direction = value;
      }
      segments[segIdx] = next;
      return { ...opt, segments, segment_count: segments.length };
    }));
  }, []);

  const commitStoryExtension = async () => {
    if (!projectId || !selectedExtendOption) {
      toast.error(extendScriptSource === 'manual' ? 'Generate scenes from your idea first' : 'Choose one of the continuation options');
      return;
    }
    const segments = Array.isArray(selectedExtendOption.segments) ? selectedExtendOption.segments : [];
    const hasScript = segments.some((seg) => String(seg?.text || seg?.scene_text || '').trim());
    if (!hasScript) {
      toast.error('Add narration script text for at least one scene');
      return;
    }
    setExtendCommitting(true);
    setExtendCommitMessage('Queuing…');
    try {
      const { data } = await api.post(`/narration/projects/${projectId}/extend-story/commit`, {
        option: selectedExtendOption,
        generate_images: true,
      });
      setExtendCommitMessage('Generating images for new scenes…');
      pollJob(data.job_id, (err) => {
        setExtendCommitting(false);
        setExtendCommitMessage('');
        if (err) {
          toast.error(err);
          return;
        }
        toast.success('New scenes added with images');
        setExtendStoryOpen(false);
        loadData();
      });
    } catch (err) {
      setExtendCommitting(false);
      setExtendCommitMessage('');
      toast.error(err.response?.data?.detail || 'Could not add extension scenes');
    }
  };

  const selectedSceneCount = selectedSceneIds.size;

  const isSongProject = useMemo(
    () => isSongBasedNarrationProject(story),
    [story],
  );

  const applySongAnimationSettings = useCallback((next) => {
    setSongAnimationEnabled(next.defaultAnimationEnabled !== false);
    setSongAnimationSpeed(next.animationSpeed ?? SONG_ANIMATION_SPEED_DEFAULT);
  }, []);

  const persistSongAnimationSettings = useCallback(
    (settings) => {
      if (!isNarrationMode || !projectId || !isSongProject) return;
      if (persistSongAnimationTimerRef.current) {
        clearTimeout(persistSongAnimationTimerRef.current);
      }
      persistSongAnimationTimerRef.current = setTimeout(async () => {
        try {
          await api.patch(
            `/narration/projects/${projectId}/song-animation-settings`,
            songAnimationSettingsToApiPayload(settings),
          );
        } catch {
          /* non-blocking */
        }
      }, 500);
    },
    [isNarrationMode, projectId, isSongProject],
  );

  const onSongAnimationSettingsChange = useCallback(
    (patch) => {
      const next = {
        defaultAnimationEnabled: patch.defaultAnimationEnabled ?? songAnimationEnabled,
        animationSpeed: patch.animationSpeed ?? songAnimationSpeed,
      };
      applySongAnimationSettings(next);
      persistSongAnimationSettings(next);
      setStory((prev) => (prev ? {
        ...prev,
        input: {
          ...(prev.input || {}),
          ...songAnimationSettingsToApiPayload(next),
        },
      } : prev));
    },
    [
      applySongAnimationSettings,
      persistSongAnimationSettings,
      songAnimationEnabled,
      songAnimationSpeed,
    ],
  );
  const canDeleteSelected = selectedSceneCount > 0 && selectedSceneCount < scenes.length;

  const directorFocusScene = useMemo(() => {
    if (selectedSceneIds.size !== 1) return { id: null, number: null };
    const sid = [...selectedSceneIds][0];
    const scene = scenes.find((s) => s.id === sid);
    if (!scene) return { id: null, number: null };
    const num = Number(scene.segment_number ?? scene.scene_number);
    return {
      id: scene.id,
      number: Number.isFinite(num) && num >= 1 ? num : null,
    };
  }, [selectedSceneIds, scenes]);

  const applyScenesAfterDelete = useCallback((data) => {
    if (isNarrationMode) {
      setScenes(narrationSegmentsFromProject({ id: projectId, segments: Array.isArray(data) ? data : [] }));
    } else {
      setScenes(Array.isArray(data) ? data : []);
    }
  }, [isNarrationMode, projectId]);

  const clearEditorStateForDeletedIds = useCallback((deletedIds) => {
    const deleted = new Set(deletedIds);
    if (editingScene && deleted.has(editingScene)) setEditingScene(null);
    if (sceneAudioTarget?.id && deleted.has(sceneAudioTarget.id)) setSceneAudioTarget(null);
    if (doodleModalScene?.id && deleted.has(doodleModalScene.id)) setDoodleModalScene(null);
    setSelectedSceneIds((prev) => {
      const next = new Set(prev);
      deletedIds.forEach((id) => next.delete(id));
      return next;
    });
  }, [editingScene, sceneAudioTarget, doodleModalScene]);

  const toggleSceneSelection = useCallback((sceneId) => {
    setSelectedSceneIds((prev) => {
      const next = new Set(prev);
      if (next.has(sceneId)) next.delete(sceneId);
      else next.add(sceneId);
      return next;
    });
  }, []);

  const selectAllScenes = useCallback(() => {
    setSelectedSceneIds(new Set(scenes.map((s) => s.id)));
  }, [scenes]);

  const clearSceneSelection = useCallback(() => {
    setSelectedSceneIds(new Set());
  }, []);

  const requestDeleteScenes = useCallback((ids) => {
    const unique = [...new Set(ids.filter(Boolean))];
    if (!unique.length) return;
    if (unique.length >= scenes.length) {
      toast.error('Cannot delete all scenes; at least one scene must remain');
      return;
    }
    setPendingDeleteIds(unique);
    setDeleteConfirmOpen(true);
  }, [scenes.length]);

  const confirmDeleteScenes = async () => {
    const ids = pendingDeleteIds;
    if (!ids.length) {
      setDeleteConfirmOpen(false);
      return;
    }
    if (demoGuard()) { setDeleteConfirmOpen(false); return; }
    setDeletingScenes(true);
    try {
      let data;
      if (isNarrationMode) {
        if (ids.length === 1) {
          ({ data } = await api.delete(`/narration/projects/${projectId}/segments/${ids[0]}`));
        } else {
          ({ data } = await api.post(`/narration/projects/${projectId}/segments/bulk-delete`, {
            segment_ids: ids,
          }));
        }
      } else if (ids.length === 1) {
        ({ data } = await api.delete(`/stories/${storyId}/scenes/${ids[0]}`));
      } else {
        ({ data } = await api.post(`/stories/${storyId}/scenes/bulk-delete`, { scene_ids: ids }));
      }
      if (isNarrationMode) {
        await loadData();
      } else {
        applyScenesAfterDelete(data);
      }
      clearEditorStateForDeletedIds(ids);
      toast.success(ids.length === 1 ? 'Scene deleted' : `${ids.length} scenes deleted`);
      setDeleteConfirmOpen(false);
      setPendingDeleteIds([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete scene(s)');
    } finally {
      setDeletingScenes(false);
    }
  };

  const pollJob = (jobId, onDone, { refreshOnComplete = true } = {}) => {
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        if (data.status === 'completed') {
          clearInterval(interval);
          if (refreshOnComplete) {
            await loadData();
          }
          onDone(null);
        } else if (data.status === 'failed') {
          clearInterval(interval);
          onDone(data.error_message || 'Generation failed');
        } else if (data.status === 'retry_scheduled') {
          clearInterval(interval);
          onDone({
            retryScheduled: true,
            message: data.error_message || 'Image provider is busy. Queued for automatic retry.',
          });
        }
      } catch (_) {}
    }, 3000);
    return interval;
  };

  const waitForJob = (jobId, { refreshOnComplete = false } = {}) =>
    new Promise((resolve) => {
      pollJob(
        jobId,
        (err) => {
          if (err && typeof err === 'object' && err.retryScheduled) {
            resolve({ ok: false, retryScheduled: true, error: err.message });
            return;
          }
          resolve({ ok: !err, error: err });
        },
        { refreshOnComplete },
      );
    });

  const generateSceneImage = async (sceneId, providerOverride) => {
    if (demoGuard()) return;
    setGeneratingImages(prev => ({ ...prev, [sceneId]: true }));
    try {
      const scene = scenes.find((s) => s.id === sceneId) || null;
      const cfgProvider =
        scene?.dialogue_scene_video?.generation_config?.provider || null;
      const selectedProvider = providerOverride
        || (isNarrationMode
          ? (narrationImageProvider || story?.image_provider || 'nano_banana')
          : (cfgProvider || story?.image_provider || 'nano_banana'));
      toast.info(`Generating image with ${imageProviderLabel(selectedProvider)}...`);
      if (isNarrationMode) {
        const { data } = await api.post(segmentApiPath(sceneId, '/regenerate-image'), {
          image_provider: selectedProvider,
          async_job: true,
        });
        const result = narrationImageGenerationResult(data);
        if (result.kind === 'retry') {
          toast.info(result.message || 'Image provider is busy. Your image was queued for automatic retry.');
          return;
        }
        if (result.kind === 'job') {
          const job = await waitForJob(result.jobId, { refreshOnComplete: true });
          if (job.retryScheduled) {
            toast.info(job.error || 'Image provider is busy. Queued for automatic retry.');
            return;
          }
          if (!job.ok) {
            toast.error(job.error || 'Failed to generate image');
            return;
          }
          toast.success('Image generated!');
          return;
        }
        if (result.kind !== 'segment') {
          toast.info(result.message || 'Image was not generated yet.');
          return;
        }
        applyNarrationSegmentUpdate(result.segment);
        await loadData();
        applyNarrationSegmentUpdate(result.segment);
        toast.success('Image generated!');
        return;
      } else {
        await api.post(`/scenes/${sceneId}/generate-image`);
        await loadData();
      }
      toast.success('Image generated!');
    } catch (err) {
      if (handleInsufficientCreditsError(err, openInsufficientCredits)) {
        /* pricing dialog shown */
      } else {
        toast.error(formatApiError(err, 'Failed to generate image'));
      }
    } finally {
      setGeneratingImages(prev => ({ ...prev, [sceneId]: false }));
    }
  };

  const generateSceneVideo = async (sceneId, provider, cameraMotion, editedPrompt) => {
    if (demoGuard()) return;
    setGeneratingVideos(prev => ({ ...prev, [sceneId]: true }));
    try {
      const engine = provider || 'nano_banana';
      const genMsg =
        engine === 'minimax'
          ? 'Generating AI video with MiniMax…'
          : engine === 'veo3'
            ? 'Generating video with Google Veo…'
            : engine === 'runway'
              ? 'Generating video with Runway…'
              : engine === 'seedance'
                ? 'Generating video with Seedance 2…'
              : engine === 'ltx'
                ? 'Generating video with LTX…'
                : 'Generating slideshow video with Nano Banana…';
      toast.info(genMsg);
      let data;
      if (isNarrationMode) {
        const videoProv = engine === 'narration_slideshow'
          ? 'narration_slideshow'
          : engine === 'nano_banana'
            ? (story?._narrationVideoProvider || 'narration_slideshow')
            : engine;
        const body = {
          async_job: true,
          video_provider: videoProv,
        };
        if (cameraMotion && cameraMotion !== 'auto') body.camera_motion = cameraMotion;
        if (editedPrompt) body.prompt = editedPrompt;
        ({ data } = await api.post(segmentApiPath(sceneId, '/generate-video'), body));
      } else {
        const body = {
          provider: engine,
          prompt: editedPrompt || undefined,
        };
        const params = {};
        if (cameraMotion && cameraMotion !== 'auto') params.camera_motion = cameraMotion;
        ({ data } = await api.post(`/scenes/${sceneId}/generate-video`, body, { params }));
      }
      toast.success('Video generation started! Hang tight...');
      pollJob(data.job_id, (err) => {
        setGeneratingVideos(prev => ({ ...prev, [sceneId]: false }));
        if (err) toast.error(err);
        else toast.success('Video ready!');
      });
    } catch (err) {
      setGeneratingVideos(prev => ({ ...prev, [sceneId]: false }));
      if (handleInsufficientCreditsError(err, openInsufficientCredits)) {
        /* pricing dialog shown */
      } else {
        toast.error(formatApiError(err, 'Failed to generate video'));
      }
    }
  };

  const saveSceneCameraDirection = async (sceneId, cameraDirection) => {
    if (demoGuard()) return;
    setSavingCameraDirection((prev) => ({ ...prev, [sceneId]: true }));
    try {
      const trimmed = (cameraDirection || '').trim();
      if (isNarrationMode) {
        await api.patch(segmentApiPath(sceneId), {
          visual: trimmed || scenes.find((s) => s.id === sceneId)?.visual || '',
        });
      } else {
        await api.put(`/scenes/${sceneId}`, { camera_direction: trimmed || null });
      }
      setScenes((prev) => prev.map((s) => (s.id === sceneId ? { ...s, camera_direction: trimmed, visual: trimmed || s.visual } : s)));
      toast.success('Camera angle saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save camera angle');
    } finally {
      setSavingCameraDirection((prev) => ({ ...prev, [sceneId]: false }));
    }
  };

  const saveSceneImagePrompt = async (sceneId, imagePrompt) => {
    if (demoGuard()) return;
    setSavingImagePrompt((prev) => ({ ...prev, [sceneId]: true }));
    try {
      const trimmed = (imagePrompt || '').trim();
      const scene = scenes.find((s) => s.id === sceneId);
      if (isNarrationMode) {
        await api.patch(segmentApiPath(sceneId), { visual: trimmed });
        setScenes((prev) =>
          prev.map((s) =>
            s.id === sceneId
              ? { ...s, visual: trimmed, camera_direction: trimmed || s.camera_direction }
              : s,
          ),
        );
      } else {
        await api.put(`/scenes/${sceneId}`, { image_prompt: trimmed || null });
        setScenes((prev) => prev.map((s) => (s.id === sceneId ? { ...s, image_prompt: trimmed } : s)));
      }
      toast.success('Image prompt saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save image prompt');
    } finally {
      setSavingImagePrompt((prev) => ({ ...prev, [sceneId]: false }));
    }
  };

  const lipSyncSceneVideo = async (sceneId, provider, cameraMotion) => {
    if (demoGuard()) return;
    if (isNarrationMode) {
      setLipSyncingVideos((prev) => ({ ...prev, [sceneId]: true }));
      try {
        const engine = provider || 'minimax';
        toast.info('Regenerating video synced to narration audio…');
        const body = {
          async_job: true,
          video_provider: engine,
        };
        if (cameraMotion && cameraMotion !== 'auto') body.camera_motion = cameraMotion;
        const { data } = await api.post(segmentApiPath(sceneId, '/generate-video'), body);
        pollJob(data.job_id, (err) => {
          setLipSyncingVideos((prev) => ({ ...prev, [sceneId]: false }));
          if (err) toast.error(err);
          else toast.success('Video ready!');
        });
      } catch (err) {
        setLipSyncingVideos((prev) => ({ ...prev, [sceneId]: false }));
        toast.error(err.response?.data?.detail || 'Failed to regenerate video');
      }
      return;
    }
    setLipSyncingVideos(prev => ({ ...prev, [sceneId]: true }));
    try {
      const engine = provider || 'seedance';
      toast.info('Generating lip-synced video with scene audio…');
      const payload = { provider: engine };
      if (cameraMotion && cameraMotion !== 'auto') payload.camera_motion = cameraMotion;
      const { data } = await api.post(`/scenes/${sceneId}/lip-sync-video`, payload);
      pollJob(data.job_id, (err) => {
        setLipSyncingVideos(prev => ({ ...prev, [sceneId]: false }));
        if (err) toast.error(err);
        else toast.success('Lip-synced video ready!');
      });
    } catch (err) {
      setLipSyncingVideos(prev => ({ ...prev, [sceneId]: false }));
      toast.error(err.response?.data?.detail || 'Failed to start lip sync generation');
    }
  };

  const expandSceneImage = async (sceneId, aspectRatio) => {
    setExpandingImages(prev => ({ ...prev, [sceneId]: true }));
    try {
      toast.info(`Expanding image to ${aspectRatio} with Runway…`);
      if (isNarrationMode) {
        const { data } = await api.post(segmentApiPath(sceneId, '/expand-image'), {
          aspect_ratio: aspectRatio,
        });
        if (data?.id) {
          applyNarrationSegmentUpdate(data);
        }
        else await loadData();
      } else {
        await api.post(`/scenes/${sceneId}/expand-image`, { aspect_ratio: aspectRatio });
        await loadData();
      }
      toast.success('Expanded image generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to expand image');
    } finally {
      setExpandingImages(prev => ({ ...prev, [sceneId]: false }));
    }
  };

  const generateSceneSoundEffect = async (sceneId, force = false) => {
    if (isNarrationMode) {
      toast.error('AI sound effects are not available for narration segments yet');
      return;
    }
    setGeneratingSceneSfx((prev) => ({ ...prev, [sceneId]: true }));
    try {
      toast.info('AI is deciding scene suitability for sound effects…');
      const { data } = await api.post(`/scenes/${sceneId}/generate-sound-effect`, { force });
      pollJob(data.job_id, (err) => {
        setGeneratingSceneSfx((prev) => ({ ...prev, [sceneId]: false }));
        if (err) toast.error(err);
        else toast.success('Scene sound effect processed.');
      });
    } catch (err) {
      setGeneratingSceneSfx((prev) => ({ ...prev, [sceneId]: false }));
      toast.error(err.response?.data?.detail || 'Failed to generate sound effect');
    }
  };

  const generateStorySoundEffects = async () => {
    try {
      setGeneratingStorySfx(true);
      toast.info('AI is evaluating all scenes for suitable sound effects…');
      const { data } = await api.post(`/stories/${storyId}/generate-sound-effects`, {});
      pollJob(data.job_id, (err) => {
        setGeneratingStorySfx(false);
        if (err) toast.error(err);
        else toast.success('Sound effects completed for suitable scenes.');
      });
    } catch (err) {
      setGeneratingStorySfx(false);
      toast.error(err.response?.data?.detail || 'Failed to start story sound effects');
    }
  };

  const selectSceneMedia = async (sceneId, kind, url) => {
    try {
      if (isNarrationMode) {
        const { data } = await api.post(segmentApiPath(sceneId, '/select-media'), { kind, url });
        if (data?.id) {
          applyNarrationSegmentUpdate(data);
          if (kind === 'audio') {
            await finalizeNarrationSegmentAudioRegeneration(projectId, data, { silent: true });
          }
        }
      } else {
        await api.post(`/scenes/${sceneId}/select-media`, { kind, url });
        await loadData();
      }
      toast.success(
        kind === 'image' ? 'Image version applied' : kind === 'audio' ? 'Audio version applied' : 'Video version applied',
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not switch version');
    }
  };

  const uploadMedia = async (sceneId, file, mediaType) => {
    try {
      toast.info(`Uploading ${mediaType}...`);
      if (isNarrationMode) {
        const form = new FormData();
        form.append('kind', mediaType);
        form.append('file', file);
        await api.post(segmentApiPath(sceneId, '/upload-media'), form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        const { data: presignedData } = await api.post('/media/presigned-url', null, {
          params: { filename: file.name, content_type: file.type },
        });
        const uploadUrl = presignedData.upload_url || presignedData.presigned_url;
        const viewUrl = presignedData.view_url || presignedData.s3_url;
        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        const updateData = mediaType === 'image'
          ? { image_url: viewUrl }
          : { video_url: viewUrl };
        await api.put(`/scenes/${sceneId}`, updateData);
      }
      toast.success(`${mediaType} uploaded!`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const videoProviderOptions = isNarrationMode
    ? NARRATION_VIDEO_PROVIDER_OPTIONS
    : STORY_VIDEO_PROVIDER_OPTIONS;

  const narrationVideoTargets = useMemo(() => {
    const ready = scenes.filter((s) => s.image_url && s.audio_url);
    const pending = ready.filter((s) => !s.video_url);
    const selected = ready.filter((s) => selectedSceneIds.has(s.id));
    return { ready, pending, selected };
  }, [scenes, selectedSceneIds]);

  const storyVideoTargets = useMemo(() => {
    const ready = scenes.filter((s) => s.image_url);
    const pending = ready.filter((s) => !s.video_url);
    const selected = ready.filter((s) => selectedSceneIds.has(s.id));
    return { ready, pending, selected };
  }, [scenes, selectedSceneIds]);

  const videoTargets = isNarrationMode ? narrationVideoTargets : storyVideoTargets;

  const runBulkVideoGeneration = async (targets, { mode = 'pending' } = {}) => {
    if (!targets.length) {
      if (isNarrationMode) {
        toast.info(mode === 'selected'
          ? 'Select scenes that have image and audio first.'
          : 'Generate images and audio for segments first.');
      } else if (mode === 'selected') {
        toast.info('Select scenes that have images first.');
      } else {
        toast.info(mode === 'all' ? 'No scenes with images yet.' : 'No scenes need video generation.');
      }
      return;
    }
    const provider = isNarrationMode ? narrationVideoProvider : batchVideoProvider;
    const ordered = [...targets].sort(
      (a, b) => (Number(a.scene_number ?? a.segment_number ?? 0) - Number(b.scene_number ?? b.segment_number ?? 0)),
    );
    setBulkVideoGenerating(true);
    setBatchGenerating(true);
    setBulkVideoProgress({ current: 0, total: ordered.length, name: '' });
    const queuedJobs = [];
    try {
      for (let i = 0; i < ordered.length; i += 1) {
        const scene = ordered[i];
        setBulkVideoProgress({
          current: i + 1,
          total: ordered.length,
          name: scene.scene_title || `Scene ${scene.scene_number ?? scene.segment_number ?? i + 1}`,
        });
        try {
          let data;
          if (isNarrationMode) {
            const videoProv = provider === 'nano_banana'
              ? (story?._narrationVideoProvider || 'narration_slideshow')
              : provider;
            ({ data } = await api.post(segmentApiPath(scene.id, '/generate-video'), {
              async_job: true,
              video_provider: videoProv,
            }));
          } else {
            ({ data } = await api.post(`/scenes/${scene.id}/generate-video`, { provider }));
          }
          if (data?.job_id) {
            queuedJobs.push({ sceneId: scene.id, jobId: data.job_id });
            setGeneratingVideos((prev) => ({ ...prev, [scene.id]: true }));
          }
        } catch (err) {
          toast.error(
            err.response?.data?.detail
              || `Failed on scene ${scene.scene_number ?? scene.segment_number ?? i + 1}`,
          );
        }
      }
      if (queuedJobs.length > 0) {
        const label = videoProviderLabel(provider);
        toast.info(
          `Generating video for ${queuedJobs.length} scene${queuedJobs.length === 1 ? '' : 's'} (${label})…`,
        );
        const results = await Promise.all(
          queuedJobs.map(async ({ sceneId, jobId }) => {
            const result = await waitForJob(jobId, { refreshOnComplete: false });
            setGeneratingVideos((prev) => ({ ...prev, [sceneId]: false }));
            return { sceneId, ...result };
          }),
        );
        await loadData();
        const failed = results.filter((r) => !r.ok);
        const succeeded = results.length - failed.length;
        if (succeeded > 0) {
          toast.success(
            `${succeeded} scene video${succeeded === 1 ? '' : 's'} ready${failed.length ? ` (${failed.length} failed)` : ''}.`,
          );
        }
        if (failed.length > 0 && succeeded === 0) {
          toast.error(failed[0].error || 'Video generation failed');
        } else if (failed.length > 0) {
          toast.error(`${failed.length} scene video${failed.length === 1 ? '' : 's'} failed`);
        }
      }
    } finally {
      setBulkVideoGenerating(false);
      setBatchGenerating(false);
      setBulkVideoProgress({ current: 0, total: 0, name: '' });
    }
  };

  const handleBulkVideoChoice = async (mode) => {
    setBulkVideoDialogOpen(false);
    if (mode === 'all') {
      await runBulkVideoGeneration(videoTargets.ready, { mode: 'all' });
    } else if (mode === 'selected') {
      await runBulkVideoGeneration(videoTargets.selected, { mode: 'selected' });
    } else {
      await runBulkVideoGeneration(videoTargets.pending, { mode: 'pending' });
    }
  };

  const openSingleGenDialog = (kind, sceneId, cameraMotion = 'auto') => {
    const defaultProvider = kind === 'image'
      ? (narrationImageProvider || story?.image_provider || 'nano_banana')
      : (isNarrationMode
        ? (narrationVideoProvider || story?._narrationVideoProvider || 'narration_slideshow')
        : 'nano_banana');
    setSingleGenProvider(defaultProvider);
    setSingleGenDialog({ kind, sceneId, cameraMotion });
  };

  const confirmSingleGenDialog = async () => {
    if (!singleGenDialog) return;
    const { kind, sceneId, cameraMotion } = singleGenDialog;
    if (kind === 'image') {
      setSingleGenDialog(null);
      const scene = scenes.find((s) => s.id === sceneId);
      try {
        const quote = await fetchCreditQuote({
          media_type: 'image',
          provider: singleGenProvider,
          duration_seconds: 1,
        });
        const est = formatCreditEstimate(quote);
        if (est) toast.info(`Estimated cost: ${est}`);
      } catch {
        /* billing optional */
      }
      await generateSceneImage(sceneId, singleGenProvider);
      return;
    }
    const reviewCtx = {
      sceneId,
      cameraMotion,
      provider: singleGenProvider,
      prompt: '',
      maxLength: 2000,
    };
    setSingleGenDialog(null);
    setVideoPromptReview(reviewCtx);
    setVideoCreditEstimate(null);
    setVideoPromptReviewLoading(true);
    try {
      const scene = scenes.find((s) => s.id === sceneId);
      const billProv = billingProviderForVideo(singleGenProvider, { isNarrationMode, story });
      if (billProv) {
        try {
          const quote = await fetchCreditQuote({
            media_type: 'video',
            provider: billProv,
            duration_seconds: scene ? sceneBillingDurationSeconds(scene) : 8,
          });
          setVideoCreditEstimate(formatCreditEstimate(quote));
        } catch {
          setVideoCreditEstimate(null);
        }
      }
      const data = await fetchVideoGenerationPrompt({
        sceneId: isNarrationMode ? undefined : sceneId,
        projectId: isNarrationMode ? story?.id : undefined,
        segmentId: isNarrationMode ? sceneId : undefined,
        provider: singleGenProvider,
        cameraMotion,
      });
      setVideoPromptReview({
        ...reviewCtx,
        prompt: data.prompt || '',
        maxLength: data.max_length || 2000,
      });
    } catch (err) {
      toast.error(formatApiError(err, 'Could not load video prompt'));
      setVideoPromptReview(null);
    } finally {
      setVideoPromptReviewLoading(false);
    }
  };

  const confirmVideoPromptReview = async (editedPrompt) => {
    if (!videoPromptReview) return;
    const { sceneId, provider, cameraMotion } = videoPromptReview;
    setVideoPromptReview(null);
    await generateSceneVideo(sceneId, provider, cameraMotion, editedPrompt);
  };

  const narrationAudioTargets = useMemo(() => {
    const withScript = scenes.filter((s) => (s.scene_text || s.narration_text || '').trim());
    const pending = withScript.filter((s) => !s.audio_url);
    const selected = withScript.filter((s) => selectedSceneIds.has(s.id));
    return { withScript, pending, selected };
  }, [scenes, selectedSceneIds]);

  const narrationImageTargets = useMemo(() => {
    const generatable = scenes.filter(sceneHasImageGenerationPrompt);
    const pending = generatable.filter((s) => !narrationSceneImageUrl(s));
    const selected = generatable.filter((s) => selectedSceneIds.has(s.id));
    return { generatable, pending, selected };
  }, [scenes, selectedSceneIds]);

  const multishotTargets = useMemo(() => {
    const withImage = scenes.filter((s) => s.image_url);
    const selected = withImage.filter((s) => selectedSceneIds.has(s.id));
    return { withImage, selected };
  }, [scenes, selectedSceneIds]);

  const narrationAdaptScriptTargets = useMemo(() => {
    const withVideoAndScript = scenes.filter((s) => (
      sceneHasVideoForAdapt(s) && sceneScriptTextForAdapt(s)
    ));
    const selected = withVideoAndScript.filter((s) => selectedSceneIds.has(s.id));
    return { withVideoAndScript, selected };
  }, [scenes, selectedSceneIds]);

  const buildAdaptScriptPayload = useCallback((scene, scriptSrc, regenerateAudio) => ({
    source_text: scriptSrc,
    persist_and_regenerate_audio: Boolean(regenerateAudio),
    use_timeline_target_seconds: false,
    ...buildNarrationAudioApiPayload(narrationAudioSettings),
  }), [narrationAudioSettings]);

  const adaptSceneScriptToVideoDuration = useCallback(async (scene, { regenerateAudio } = {}) => {
    if (!isNarrationMode || !projectId) return false;
    const scriptSrc = sceneScriptTextForAdapt(scene);
    if (!scriptSrc) {
      toast.error('No script text on this segment to adapt.');
      return false;
    }
    if (!sceneHasVideoForAdapt(scene)) {
      toast.error('Generate a video for this segment first.');
      return false;
    }
    setAdaptingScriptSceneId(scene.id);
    try {
      const { data } = await api.post(
        segmentApiPath(scene.id, '/adapt-script-to-video-duration'),
        buildAdaptScriptPayload(scene, scriptSrc, regenerateAudio),
      );
      if (data?.id) {
        applyNarrationSegmentUpdate(data);
        if (regenerateAudio) {
          try {
            await finalizeNarrationSegmentAudioRegeneration(projectId, data, { silent: true });
          } catch {
            /* non-fatal */
          }
        }
        const durNote = data.target_seconds_used != null
          ? ` (~${Number(data.target_seconds_used).toFixed(1)}s video)`
          : '';
        toast.success(
          regenerateAudio
            ? `Script adapted${durNote} and narration audio regenerated.`
            : `Script adapted${durNote} and saved.`,
        );
        return true;
      }
      const next = (data?.text || '').trim();
      if (!next) throw new Error('Empty adaptation');
      await api.patch(segmentApiPath(scene.id), {
        text: next,
        visual: scene.visual || next,
        emotion: scene.emotion,
        phoneme_notes: scene.phoneme_notes || '',
      });
      setScenes((prev) => prev.map((s) => (
        s.id === scene.id ? { ...s, scene_text: next, narration_text: next, text: next } : s
      )));
      const durNote = data.target_seconds_used != null
        ? ` (~${Number(data.target_seconds_used).toFixed(1)}s video)`
        : '';
      toast.success(`Script adapted${durNote} and saved.`);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Could not adapt script to video length');
      return false;
    } finally {
      setAdaptingScriptSceneId(null);
    }
  }, [
    isNarrationMode,
    projectId,
    segmentApiPath,
    buildAdaptScriptPayload,
    applyNarrationSegmentUpdate,
  ]);

  const runBulkAdaptScriptToVideo = async (targets) => {
    if (demoGuard()) return;
    if (!projectId) {
      toast.error('Narration project not loaded yet');
      return;
    }
    if (!targets.length) {
      toast.info('No segments with both script text and a generated video.');
      return;
    }
    const ordered = [...targets].sort(
      (a, b) => (Number(a.scene_number ?? a.segment_number ?? 0) - Number(b.scene_number ?? b.segment_number ?? 0)),
    );
    setBulkAdaptScriptGenerating(true);
    setBulkAdaptScriptProgress({ current: 0, total: ordered.length, name: '' });
    let ok = 0;
    try {
      for (let i = 0; i < ordered.length; i += 1) {
        const scene = ordered[i];
        setBulkAdaptScriptProgress({
          current: i + 1,
          total: ordered.length,
          name: scene.scene_title || `Segment ${scene.scene_number ?? i + 1}`,
        });
        const done = await adaptSceneScriptToVideoDuration(scene, {
          regenerateAudio: adaptScriptRegenerateAudio,
        });
        if (done) ok += 1;
      }
      if (ok > 0) {
        toast.success(
          `Adapted script for ${ok} segment${ok === 1 ? '' : 's'} to match video length.`,
        );
      }
    } finally {
      setBulkAdaptScriptGenerating(false);
      setBulkAdaptScriptProgress({ current: 0, total: 0, name: '' });
    }
  };

  const handleBulkAdaptScriptChoice = async (mode) => {
    setBulkAdaptScriptDialogOpen(false);
    if (mode === 'selected') {
      await runBulkAdaptScriptToVideo(narrationAdaptScriptTargets.selected);
    } else {
      await runBulkAdaptScriptToVideo(narrationAdaptScriptTargets.withVideoAndScript);
    }
  };

  const runBulkNarrationAudioGeneration = async (targets, { mode = 'pending' } = {}) => {
    if (demoGuard()) return;
    if (!projectId) {
      toast.error('Narration project not loaded yet');
      return;
    }
    if (!targets.length) {
      if (mode === 'selected') {
        toast.info('Select one or more scenes with script text first.');
      } else if (mode === 'all') {
        toast.info('No segments with script text.');
      } else {
        toast.info('No segments need audio generation.');
      }
      return;
    }
    setBulkAudioGenerating(true);
    setBulkAudioProgress({ current: 0, total: targets.length, name: '' });
    const updatedSegments = [];
    try {
      for (let i = 0; i < targets.length; i += 1) {
        const scene = targets[i];
        setBulkAudioProgress({
          current: i + 1,
          total: targets.length,
          name: scene.scene_title || `Segment ${scene.scene_number ?? i + 1}`,
        });
        const script = (scene.scene_text || scene.narration_text || '').trim();
        const { data } = await api.post(
          segmentApiPath(scene.id, '/regenerate-audio'),
          buildNarrationAudioPayload({ prompt: script }),
        );
        if (data?.id) {
          applyNarrationSegmentUpdate(data);
          updatedSegments.push(data);
        }
      }
      if (updatedSegments.length) {
        await finalizeNarrationSegmentAudioRegeneration(projectId, updatedSegments, { silent: true });
      }
      toast.success(
        mode === 'all'
          ? `Regenerated audio for ${targets.length} segment${targets.length === 1 ? '' : 's'}.`
          : mode === 'selected'
            ? `Generated audio for ${targets.length} selected segment${targets.length === 1 ? '' : 's'}.`
            : `Generated audio for ${targets.length} pending segment${targets.length === 1 ? '' : 's'}.`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate narration audio');
    } finally {
      setBulkAudioGenerating(false);
      setBulkAudioProgress({ current: 0, total: 0, name: '' });
    }
  };

  const handleBulkNarrationAudioChoice = async (mode) => {
    setBulkAudioDialogOpen(false);
    if (mode === 'all') {
      await runBulkNarrationAudioGeneration(narrationAudioTargets.withScript, { mode: 'all' });
    } else if (mode === 'selected') {
      await runBulkNarrationAudioGeneration(narrationAudioTargets.selected, { mode: 'selected' });
    } else {
      await runBulkNarrationAudioGeneration(narrationAudioTargets.pending, { mode: 'pending' });
    }
  };

  const runBulkNarrationImageGeneration = async (targets, { mode = 'pending' } = {}) => {
    if (demoGuard()) return;
    if (!projectId) {
      toast.error('Narration project not loaded yet');
      return;
    }
    if (!targets.length) {
      if (mode === 'selected') {
        toast.info('Select one or more scenes with script or image prompts first.');
      } else if (mode === 'all') {
        toast.info('No segments with script or image prompts.');
      } else {
        toast.info('No segments need image generation.');
      }
      return;
    }
    const provider = narrationImageProvider || story?.image_provider || 'nano_banana';
    setBulkImageGenerating(true);
    setBulkImageProgress({ current: 0, total: targets.length, name: '' });
    const generatingIds = Object.fromEntries(targets.map((s) => [s.id, true]));
    setGeneratingImages((prev) => ({ ...prev, ...generatingIds }));
    let successCount = 0;
    let completedCount = 0;
    const successSegments = [];
    try {
      toast.info(
        `Generating ${targets.length} image(s) in parallel (up to ${NARRATION_IMAGE_BATCH_CONCURRENCY} at a time) with ${imageProviderLabel(provider)}…`,
      );
      await mapWithConcurrency(targets, NARRATION_IMAGE_BATCH_CONCURRENCY, async (scene, index) => {
        const sceneLabel = scene.scene_title
          || `Scene ${scene.segment_number ?? scene.scene_number ?? index + 1}`;
        try {
          const { data } = await api.post(segmentApiPath(scene.id, '/regenerate-image'), {
            image_provider: provider,
            async_job: true,
          });
          const result = narrationImageGenerationResult(data);
          if (result.kind === 'retry') {
            toast.info(
              result.message || `Scene ${scene.segment_number ?? scene.scene_number ?? index + 1} queued for retry`,
            );
            return;
          }
          if (result.kind !== 'job' && result.kind !== 'segment') {
            toast.info(result.message || `Scene ${scene.segment_number ?? scene.scene_number ?? index + 1} was not generated yet.`);
            return;
          }
          if (result.kind === 'job') {
            const job = await waitForJob(result.jobId, { refreshOnComplete: false });
            if (job.retryScheduled) {
              toast.info(
                job.error
                  || `Scene ${scene.segment_number ?? scene.scene_number ?? index + 1} queued for retry`,
              );
              return;
            }
            if (!job.ok) {
              toast.error(
                job.error
                  || `Failed on scene ${scene.segment_number ?? scene.scene_number ?? index + 1}`,
              );
              return;
            }
          } else if (result.kind === 'segment') {
            applyNarrationSegmentUpdate(result.segment);
            successSegments.push(result.segment);
          }
          successCount += 1;
        } catch (err) {
          toast.error(
            err.response?.data?.detail
              || `Failed on scene ${scene.segment_number ?? scene.scene_number ?? index + 1}`,
          );
        } finally {
          completedCount += 1;
          setBulkImageProgress({
            current: completedCount,
            total: targets.length,
            name: sceneLabel,
          });
          setGeneratingImages((prev) => ({ ...prev, [scene.id]: false }));
        }
      });
      await loadData();
      successSegments.forEach((segment) => applyNarrationSegmentUpdate(segment));
      if (successCount === targets.length) {
        toast.success(
          mode === 'all'
            ? `Generated images for all ${successCount} scene${successCount === 1 ? '' : 's'}. Previous images saved as versions.`
            : mode === 'selected'
              ? `Generated images for ${successCount} selected scene${successCount === 1 ? '' : 's'}. Previous images saved as versions.`
              : `Generated images for ${successCount} pending scene${successCount === 1 ? '' : 's'}.`,
        );
      } else if (successCount > 0) {
        toast.warning(`Generated ${successCount} of ${targets.length} images.`);
      }
    } finally {
      setBulkImageGenerating(false);
      setBulkImageProgress({ current: 0, total: 0, name: '' });
    }
  };

  const handleBulkNarrationImageChoice = async (mode) => {
    setBulkImageDialogOpen(false);
    if (mode === 'all') {
      await runBulkNarrationImageGeneration(narrationImageTargets.generatable, { mode: 'all' });
    } else if (mode === 'selected') {
      await runBulkNarrationImageGeneration(narrationImageTargets.selected, { mode: 'selected' });
    } else {
      await runBulkNarrationImageGeneration(narrationImageTargets.pending, { mode: 'pending' });
    }
  };

  const runBulkMultishotGeneration = async (targets) => {
    if (demoGuard()) return;
    if (!projectId) {
      toast.error('Project not loaded yet');
      return;
    }
    if (!targets.length) {
      toast.info('Select scenes that already have a master image.');
      return;
    }
    const count = Math.max(1, Math.min(8, Number(multishotCount) || 2));
    const provider = narrationImageProvider || story?.image_provider || 'nano_banana';
    const promptTemplate = String(multishotPromptTemplate || '').trim();
    setBulkMultishotGenerating(true);
    try {
      const body = {
        segment_ids: targets.map((scene) => scene.id),
        shot_count: count,
        image_provider: provider,
        replace_existing: true,
      };
      if (promptTemplate) body.prompt_suffix_template = promptTemplate;
      const { data } = await api.post(`/narration/projects/${projectId}/segments/multishot`, body);
      if (!data?.job_id) {
        toast.error('Multishot job was not queued.');
        return;
      }
      setBulkMultishotDialogOpen(false);
      toast.success(
        data.message
          || `Queued ${count} multishot(s) for ${targets.length} scene(s) — check Tasks for progress.`,
      );
      pollMultishotJobProgress(data.job_id, targets);
    } catch (err) {
      if (handleInsufficientCreditsError(err, openInsufficientCredits)) {
        /* pricing dialog shown */
      } else {
        toast.error(formatApiError(err, 'Failed to queue multishot generation'));
      }
    } finally {
      setBulkMultishotGenerating(false);
    }
  };

  const openMultishotDialog = () => {
    setMultishotPromptTemplate(DEFAULT_MULTISHOT_PROMPT_TEMPLATE);
    setBulkMultishotDialogOpen(true);
  };

  const handleBulkMultishotChoice = async (mode) => {
    if (mode === 'selected') {
      await runBulkMultishotGeneration(multishotTargets.selected);
    } else {
      await runBulkMultishotGeneration(multishotTargets.withImage);
    }
  };

  const persistMultishotTimelineSelection = useCallback(async (sceneId, patch) => {
    if (!projectId || !sceneId) return;
    const scene = scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    setMultishotSelectionSavingId(sceneId);
    try {
      const { data } = await api.patch(
        `/narration/projects/${projectId}/segments/${sceneId}/multishot-selection`,
        patch,
      );
      if (data?.id) applyNarrationSegmentUpdate(data);
      else applyNarrationSegmentUpdate(applyMultishotSelectionPatch(scene, patch));
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('twin-tales-multishot-selection-changed', {
          detail: { projectId, sceneId },
        }));
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to update multishot timeline selection'));
    } finally {
      setMultishotSelectionSavingId(null);
    }
  }, [projectId, scenes, applyNarrationSegmentUpdate]);

  const handleMultishotMasterToggle = useCallback(async (sceneId, enabled) => {
    const scene = scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    const payload = multishotSelectionPayloadFromScene(scene);
    await persistMultishotTimelineSelection(sceneId, {
      include_master: enabled,
      shots: payload.shots,
    });
  }, [scenes, persistMultishotTimelineSelection]);

  const handleMultishotShotToggle = useCallback(async (sceneId, shotIndex, enabled) => {
    const scene = scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    const payload = multishotSelectionPayloadFromScene(scene);
    const shots = payload.shots.map((row) => (
      Number(row.index) === Number(shotIndex) ? { ...row, enabled } : row
    ));
    await persistMultishotTimelineSelection(sceneId, {
      include_master: payload.include_master,
      shots,
    });
  }, [scenes, persistMultishotTimelineSelection]);

  const generateStoryDialogueAudio = async () => {
    try {
      setStoryAudioGenerating(true);
      if (isNarrationMode) {
        setBulkAudioDialogOpen(true);
        setStoryAudioGenerating(false);
        return;
      }
      const payload = {
        voice_style: storyAudioVoice,
        save_per_character_clips: savePerLineAudioClips,
        language: storyAudioLanguage,
      };
      if (storyNarratorMinimax.trim()) {
        payload.narrator = { minimax_voice_id: storyNarratorMinimax.trim() };
      }
      if (modalNarratorTtsProvider === 'runway') {
        payload.narrator = {
          ...(payload.narrator || {}),
          runway_model: modalNarratorRunwayModel || 'eleven_multilingual_v2',
        };
        payload.narrator_tts_provider = 'runway';
      }
      const cv = {};
      for (const c of story?.characters || []) {
        if (!c?.id) continue;
        const o = {};
        const mm = (modalCharMinimax[c.id] || '').trim();
        const mf = (modalCharMurf[c.id] || '').trim();
        const mloc = (modalCharMurfLocale[c.id] || '').trim();
        const el = (modalCharEleven[c.id] || '').trim();
        const dprov = modalCharDialogueProvider[c.id] || 'auto';
        const rw = modalCharRunwayModel[c.id] || 'eleven_multilingual_v2';
        if (mm) o.minimax_voice_id = mm;
        if (mf) o.murf_voice_id = mf;
        if (mloc) o.murf_locale = mloc;
        if (el) o.elevenlabs_voice_id = el;
        if (dprov && dprov !== 'auto') o.dialogue_tts_provider = dprov;
        if (dprov === 'runway') o.runway_model = rw;
        if (Object.keys(o).length) cv[c.id] = o;
      }
      if (Object.keys(cv).length) payload.character_voices = cv;
      const { data } = await api.post(`/stories/${storyId}/generate-audio`, payload);
      toast.success('Generating per-scene dialogue & narration audio…');
      pollJob(data.job_id, (err) => {
        setStoryAudioGenerating(false);
        if (err) toast.error(err);
        else {
          toast.success('Scene audio and timeline are ready.');
          loadData();
        }
      });
    } catch (err) {
      setStoryAudioGenerating(false);
      toast.error(err.response?.data?.detail || 'Failed to start audio generation');
    }
  };

  const createDubbedStoryVersion = async () => {
    try {
      setStoryAudioDubGenerating(true);
      const payload = {
        target_language: dubTargetLanguage,
        disable_voice_cloning: dubDisableVoiceCloning,
        drop_background_audio: dubDropBackgroundAudio,
      };
      const { data } = await api.post(`/stories/${storyId}/audio-dub-version`, payload);
      toast.success('Dubbing started. Creating a new story version…');
      pollJob(data.job_id, async (err) => {
        setStoryAudioDubGenerating(false);
        if (err) {
          toast.error(err);
          return;
        }
        try {
          const { data: job } = await api.get(`/jobs/${data.job_id}`);
          const newStoryId = job?.result_data?.new_story_id;
          if (newStoryId) {
            toast.success('Dubbed version is ready. Opening new project…');
            navigate(`/stories/${newStoryId}/edit`);
          } else {
            toast.success('Dubbed version completed.');
          }
        } catch {
          toast.success('Dubbed version completed.');
        }
      });
    } catch (err) {
      setStoryAudioDubGenerating(false);
      toast.error(err.response?.data?.detail || 'Failed to start voice dubbing');
    }
  };

  const submitSceneAudioGeneration = async () => {
    if (!sceneAudioTarget?.id) return;
    const scene = sceneAudioTarget;
    const payload = {
      voice_style: modalAudioVoice,
      save_per_character_clips: savePerLineAudioClips,
      language: modalAudioLanguage,
    };
    const narrator = {};
    if (modalNarratorMinimax.trim()) narrator.minimax_voice_id = modalNarratorMinimax.trim();
    if (modalNarratorMurf.trim()) narrator.murf_voice_id = modalNarratorMurf.trim();
    if (modalNarratorMurfLocale.trim()) narrator.murf_locale = modalNarratorMurfLocale.trim();
    if (modalNarratorEleven.trim()) narrator.elevenlabs_voice_id = modalNarratorEleven.trim();
    if (Object.keys(narrator).length) payload.narrator = narrator;
    if (modalNarratorTtsProvider === 'runway') {
      payload.narrator = {
        ...(payload.narrator || {}),
        runway_model: modalNarratorRunwayModel || 'eleven_multilingual_v2',
      };
    }

    if (modalNarratorTtsProvider && modalNarratorTtsProvider !== 'auto') {
      payload.narrator_tts_provider = modalNarratorTtsProvider;
    }
    if (modalDialogueTtsDefault && modalDialogueTtsDefault !== 'auto') {
      payload.dialogue_tts_default = modalDialogueTtsDefault;
    }

    const cv = {};
    for (const c of story?.characters || []) {
      const cid = c?.id;
      if (!cid) continue;
      const o = {};
      const mm = (modalCharMinimax[cid] || '').trim();
      const mf = (modalCharMurf[cid] || '').trim();
      const mloc = (modalCharMurfLocale[cid] || '').trim();
      const el = (modalCharEleven[cid] || '').trim();
      const dprov = modalCharDialogueProvider[cid] || 'auto';
      const rw = modalCharRunwayModel[cid] || 'eleven_multilingual_v2';
      if (mm) o.minimax_voice_id = mm;
      if (mf) o.murf_voice_id = mf;
      if (mloc) o.murf_locale = mloc;
      if (el) o.elevenlabs_voice_id = el;
      if (dprov && dprov !== 'auto') o.dialogue_tts_provider = dprov;
      if (dprov === 'runway') o.runway_model = rw;
      if (Object.keys(o).length) cv[cid] = o;
    }
    if (Object.keys(cv).length) payload.character_voices = cv;
    payload.audio_script = scriptDraftToPayload(sceneScriptDraft, characterById);

    setSceneAudioGeneratingId(scene.id);
    setSceneAudioTarget(null);
    try {
      if (isNarrationMode) {
        const scriptText = sceneScriptDraft
          .map((seg) => (seg?.text || '').trim())
          .filter(Boolean)
          .join('\n')
          || scene.scene_text
          || '';
        if (scriptText) {
          await api.patch(segmentApiPath(scene.id), {
            text: scriptText,
            visual: scene.visual || scriptText,
            emotion: scene.emotion,
            phoneme_notes: scene.phoneme_notes || '',
          });
        }
        const { data } = await api.post(
          segmentApiPath(scene.id, '/regenerate-audio'),
          buildNarrationAudioPayload({ prompt: scriptText }),
        );
        if (data?.id) {
          applyNarrationSegmentUpdate(data);
          await finalizeNarrationSegmentAudioRegeneration(projectId, data, { silent: true });
        }
        setSceneAudioGeneratingId(null);
        toast.success('Narration audio regenerated for this scene');
        return;
      }
      const { data } = await api.post(
        `/stories/${storyId}/scenes/${scene.id}/generate-audio`,
        payload,
      );
      toast.success('Generating audio for this scene…');
      pollJob(data.job_id, (err) => {
        setSceneAudioGeneratingId(null);
        if (err) toast.error(err);
        else {
          toast.success('Scene audio is ready.');
          loadData();
        }
      });
    } catch (err) {
      setSceneAudioGeneratingId(null);
      toast.error(err.response?.data?.detail || 'Failed to start scene audio');
    }
  };

  const handleExportStory = async () => {
    try {
      setExportingStory(true);
      setExportedVideoUrl(null);
      toast.info('Compiling story video…');
      const { data } = await api.post(`/stories/${storyId}/export-video`);
      pollJob(data.job_id, (err) => {
        setExportingStory(false);
        if (err) {
          toast.error(err);
        } else {
          // Reload job to get result_url
          api.get(`/jobs/${data.job_id}`).then(({ data: job }) => {
            if (job.result_url) {
              setExportedVideoUrl(job.result_url);
              toast.success('Story video ready! Click Download to save.');
            }
          });
        }
      });
    } catch (err) {
      setExportingStory(false);
      toast.error(err.response?.data?.detail || 'Export failed');
    }
  };

  const handleExportPremiere = async () => {
    try {
      setExportingPremiere(true);
      toast.info('Building Premiere Pro package…');
      const { data } = await api.post(`/stories/${storyId}/export-premiere`);
      pollJob(data.job_id, async (err) => {
        setExportingPremiere(false);
        if (err) {
          toast.error(err || 'Premiere export failed');
          return;
        }
        try {
          const { data: job } = await api.get(`/jobs/${data.job_id}`);
          if (job.result_url) {
            const a = document.createElement('a');
            a.href = job.result_url;
            const slug = (story?.title || 'premiere').replace(/[^\w\-]+/g, '_').slice(0, 40);
            a.download = `${slug}-premiere.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            toast.success('Premiere package ready! Unzip, then use File → Import in Premiere Pro.');
          }
        } catch (_) {
          toast.info('Premiere package done — check Task history to download.');
        }
      }, { refreshOnComplete: false });
    } catch (err) {
      setExportingPremiere(false);
      toast.error(err.response?.data?.detail || 'Premiere export failed');
    }
  };

  if (loading && !story) {
    return (
      <div
        className="flex items-center justify-center min-h-screen font-jakarta"
        style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)' }}
      >
        <Loader2 className="w-8 h-8 animate-spin text-[#62FAE3]" />
      </div>
    );
  }

  const scenesWithImages = scenes.filter((s) => narrationSceneImageUrl(s)).length;
  const scenesWithVideos = scenes.filter(s => s.video_url).length;
  const scenesWithAudio = scenes.filter(s => s.audio_url).length;
  const pendingNarrationAudioCount = narrationAudioTargets.pending.length;
  const allNarrationAudioCount = narrationAudioTargets.withScript.length;
  const selectedNarrationAudioCount = narrationAudioTargets.selected.length;
  const allNarrationAdaptScriptCount = narrationAdaptScriptTargets.withVideoAndScript.length;
  const selectedNarrationAdaptScriptCount = narrationAdaptScriptTargets.selected.length;
  const pendingNarrationImageCount = narrationImageTargets.pending.length;
  const allNarrationImageCount = narrationImageTargets.generatable.length;
  const selectedNarrationImageCount = narrationImageTargets.selected.length;
  const pendingNarrationVideoCount = narrationVideoTargets.pending.length;
  const allNarrationVideoCount = narrationVideoTargets.ready.length;
  const selectedNarrationVideoCount = narrationVideoTargets.selected.length;
  const pendingStoryVideoCount = storyVideoTargets.pending.length;
  const allStoryVideoCount = storyVideoTargets.ready.length;
  const selectedStoryVideoCount = storyVideoTargets.selected.length;
  const pendingVideoCount = isNarrationMode ? pendingNarrationVideoCount : pendingStoryVideoCount;
  const allVideoCount = isNarrationMode ? allNarrationVideoCount : allStoryVideoCount;
  const selectedVideoCount = isNarrationMode ? selectedNarrationVideoCount : selectedStoryVideoCount;

  return (
    <div
      className="min-h-screen flex flex-col pb-20 font-jakarta text-[var(--text-primary)]"
      style={{ background: 'var(--bg-primary)' }}
    >
      <EditorTopNav
        active="scene"
        mode={isNarrationMode ? 'narration' : 'story'}
        projectId={projectId}
        storyId={storyId}
        title={story?.title || ''}
        subtitle={`${scenes.length} scene${scenes.length === 1 ? '' : 's'}`}
      />

      {/* Scene tools — page-specific actions under the shared top nav */}
      <div
        className="border-b shrink-0"
        style={{ borderColor: 'var(--glass-border)', background: 'rgba(13, 16, 22, 0.72)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex w-full min-w-0 flex-col gap-3 xl:flex-row xl:flex-wrap xl:items-center xl:justify-end">
            <div className="flex flex-wrap items-center justify-end gap-2">
              <div
                className="flex items-center gap-2 rounded-lg border px-3 py-1.5"
                style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' }}
                title="Show or hide the Movie Director side panel"
              >
                <Switch
                  id="scene-grid-movie-director-toggle"
                  checked={movieDirectorOpen}
                  onCheckedChange={setMovieDirectorOpen}
                  data-testid="movie-director-panel-toggle"
                />
                <Label
                  htmlFor="scene-grid-movie-director-toggle"
                  className="cursor-pointer text-sm font-medium whitespace-nowrap"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Movie Director
                </Label>
              </div>
              <StorySoundToggle
                visible={storySound.visible}
                open={soundPanelOpen}
                onOpenChange={setSoundPanelOpen}
              />
              <Button
                onClick={() => setStoryAudioPanelOpen(true)}
                variant="outline"
                className="rounded-lg"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                data-testid="open-story-audio-settings-btn"
              >
                <Mic className="w-4 h-4 mr-2" /> Audio settings & progress
              </Button>
              <Button
                onClick={() => navigate(
                  isNarrationMode
                    ? `/narration/${projectId}/video-editor`
                    : `/stories/${storyId}/video-editor`,
                )}
                variant="outline"
                className="rounded-lg"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                data-testid="edit-video-btn"
              >
                <Film className="w-4 h-4 mr-2" /> Edit Video
              </Button>

              {!isNarrationMode ? (
              <Button
                type="button"
                onClick={openAddSceneDialog}
                variant="outline"
                className="rounded-lg"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                data-testid="add-scene-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add scene
              </Button>
              ) : (
              <>
                <NarrationAudioSettingsBar
                  settings={narrationAudioSettings}
                  onChange={onNarrationAudioSettingsChange}
                  murfVoices={murfVoicesCatalog}
                  onChooseVoice={() => {
                    if (storyTtsProvider === 'auto') {
                      toast.info('Choose ElevenLabs, Murf, or MiniMax as the audio provider first');
                      return;
                    }
                    setNarrationVoicePickerOpen(true);
                  }}
                  disabled={bulkAudioGenerating || bulkImageGenerating}
                />
                <Button
                  type="button"
                  onClick={openExtendStoryDialog}
                  variant="outline"
                  className="rounded-lg"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  data-testid="extend-narration-story-btn"
                >
                  <Clapperboard className="w-4 h-4 mr-2" /> Extend story
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setBulkAudioDialogOpen(true)}
                  disabled={bulkAudioGenerating || bulkImageGenerating || bulkAdaptScriptGenerating || allNarrationAudioCount === 0}
                  className="rounded-lg"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  data-testid="generate-bulk-narration-audio-btn"
                >
                  {bulkAudioGenerating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Audio {bulkAudioProgress.current}/{bulkAudioProgress.total}</>
                  ) : (
                    <><Volume2 className="w-4 h-4 mr-2" /> Generate audio{pendingNarrationAudioCount ? ` (${pendingNarrationAudioCount} pending)` : ''}</>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setBulkAdaptScriptDialogOpen(true)}
                  disabled={
                    bulkAdaptScriptGenerating
                    || bulkAudioGenerating
                    || bulkImageGenerating
                    || allNarrationAdaptScriptCount === 0
                  }
                  className="rounded-lg"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  data-testid="bulk-adapt-script-to-video-btn"
                  title="AI rewrites narration script to fit each segment video file length"
                >
                  {bulkAdaptScriptGenerating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scripts {bulkAdaptScriptProgress.current}/{bulkAdaptScriptProgress.total}</>
                  ) : (
                    <><FileText className="w-4 h-4 mr-2" /> Match script to video</>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setBulkImageDialogOpen(true)}
                  disabled={bulkImageGenerating || bulkAudioGenerating || bulkVideoGenerating || allNarrationImageCount === 0}
                  className="rounded-lg"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  data-testid="generate-bulk-narration-images-btn"
                >
                  {bulkImageGenerating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Images {bulkImageProgress.current}/{bulkImageProgress.total}</>
                  ) : (
                    <><ImageIcon className="w-4 h-4 mr-2" /> Generate images{pendingNarrationImageCount ? ` (${pendingNarrationImageCount} pending)` : ''}</>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={openMultishotDialog}
                  disabled={
                    bulkMultishotGenerating
                    || bulkImageGenerating
                    || multishotTargets.withImage.length === 0
                  }
                  className="rounded-lg"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  data-testid="generate-multishots-btn"
                  title="Queue alternate camera angles per scene (async — check Tasks)"
                >
                  {bulkMultishotGenerating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Queueing multishots…</>
                  ) : (
                    <><TwinneeLogo className="w-4 h-4 mr-2" /> Multishots{selectedSceneCount ? ` (${selectedSceneCount} selected)` : ''}</>
                  )}
                </Button>
                {isSongProject ? (
                  <>
                    <div
                      className="flex flex-col gap-2 rounded-lg border px-3 py-2 min-w-[220px]"
                      style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' }}
                      data-testid="song-animation-settings-panel"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <Label
                          htmlFor="song-default-animation-toggle"
                          className="text-xs font-medium whitespace-nowrap"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          Beat-sync animation
                        </Label>
                        <Switch
                          id="song-default-animation-toggle"
                          checked={songAnimationEnabled}
                          onCheckedChange={(checked) => onSongAnimationSettingsChange({
                            defaultAnimationEnabled: checked,
                          })}
                          data-testid="song-default-animation-toggle"
                        />
                      </div>
                      <div className={songAnimationEnabled ? '' : 'opacity-45 pointer-events-none'}>
                        <div className="flex items-center justify-between text-[10px] mb-1"
                          style={{ color: 'var(--text-secondary)' }}>
                          <span>Animation speed</span>
                          <span>{songAnimationSpeedLabel(songAnimationSpeed)}</span>
                        </div>
                        <Slider
                          value={[songAnimationSpeedToSlider(songAnimationSpeed)]}
                          min={MOTION_SPEED_SLIDER_MIN}
                          max={MOTION_SPEED_SLIDER_MAX}
                          step={1}
                          onValueChange={([v]) => onSongAnimationSettingsChange({
                            animationSpeed: songAnimationSpeedFromSlider(v),
                          })}
                          data-testid="song-animation-speed-slider"
                        />
                      </div>
                    </div>
                  </>
                ) : null}
              </>
              )}

              <Button
                type="button"
                onClick={() => setBulkVideoDialogOpen(true)}
                disabled={
                  batchGenerating
                  || bulkVideoGenerating
                  || bulkImageGenerating
                  || bulkAudioGenerating
                  || allVideoCount === 0
                }
                style={{ background: '#f97316', color: 'white' }}
                className="rounded-lg"
                data-testid="generate-videos-btn"
              >
                {batchGenerating || bulkVideoGenerating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Videos {bulkVideoProgress.current}/{bulkVideoProgress.total || '…'}</>
                ) : (
                  <><Play className="w-4 h-4 mr-2" /> Generate videos{pendingVideoCount ? ` (${pendingVideoCount} pending)` : ''}</>
                )}
              </Button>

              {/* Export full story video */}
              {!isNarrationMode && exportedVideoUrl ? (
                <a
                  href={exportedVideoUrl}
                  download={`${story?.title || 'story'}.mp4`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="download-story-video-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
                  style={{ background: '#22c55e', color: 'white' }}
                >
                  <Download className="w-4 h-4" /> Download Video
                </a>
              ) : !isNarrationMode ? (
                <Button
                  onClick={handleExportStory}
                  disabled={exportingStory || scenesWithVideos === 0}
                  variant="outline"
                  className="rounded-lg"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  data-testid="export-story-btn"
                >
                  {exportingStory ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Compiling...</>
                  ) : (
                    <><Download className="w-4 h-4 mr-2" /> Export Story</>
                  )}
                </Button>
              ) : null}

              {/* Export editable Premiere package */}
              {!isNarrationMode ? (
                <Button
                  onClick={handleExportPremiere}
                  disabled={exportingPremiere || exportingStory || scenesWithVideos === 0}
                  variant="outline"
                  className="rounded-lg gap-2"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  data-testid="export-premiere-btn"
                  title="Download an editable Premiere Pro package (one clip per scene). Unzip, then File → Import."
                >
                  {exportingPremiere ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Clapperboard className="w-4 h-4" />
                  )}
                  {exportingPremiere ? 'Building…' : 'Premiere Pro'}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {/* Story Header */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          {story?.user_topic ? (
            <p style={{ color: 'var(--text-secondary)' }}>{story.user_topic}</p>
          ) : null}
        </div>

        {/* Scenes Grid */}
        <motion.div className={`max-w-7xl mx-auto px-6 ${movieDirectorOpen ? 'pb-24' : 'pb-10'}`}>
          {scenes.length > 0 ? (
            <div
              className="flex flex-wrap items-center gap-2 mb-4 py-2 px-3 rounded-xl border"
              style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
            >
              {selectedSceneCount > 0 ? (
                <span className="text-xs font-medium mr-1" style={{ color: 'var(--text-secondary)' }}>
                  {selectedSceneCount} selected
                </span>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-lg h-8"
                onClick={selectAllScenes}
                disabled={deletingScenes || selectedSceneCount === scenes.length}
                data-testid="scene-select-all-btn"
              >
                Select all
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-lg h-8"
                onClick={clearSceneSelection}
                disabled={deletingScenes || selectedSceneCount === 0}
                data-testid="scene-clear-selection-btn"
              >
                Clear selection
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-lg h-8 gap-1.5"
                style={{ borderColor: '#fecaca', color: '#ef4444' }}
                onClick={() => requestDeleteScenes([...selectedSceneIds])}
                disabled={deletingScenes || !canDeleteSelected}
                data-testid="scene-delete-selected-btn"
              >
                {deletingScenes ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5" />
                )}
                Delete selected{selectedSceneCount > 0 ? ` (${selectedSceneCount})` : ''}
              </Button>
            </div>
          ) : null}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {scenesLoading && scenes.length === 0 ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={`scene-skel-${i}`}
                  className="rounded-xl border overflow-hidden animate-pulse"
                  style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
                  data-testid="scene-card-skeleton"
                >
                  <div className="aspect-video" style={{ background: 'var(--bg-tertiary)' }} />
                  <div className="p-3 space-y-2">
                    <div className="h-3 w-1/3 rounded" style={{ background: 'var(--bg-tertiary)' }} />
                    <div className="h-3 w-full rounded" style={{ background: 'var(--bg-tertiary)' }} />
                    <div className="h-3 w-2/3 rounded" style={{ background: 'var(--bg-tertiary)' }} />
                  </div>
                </div>
              ))
            ) : null}
            {scenes.map((scene, idx) => (
              <SceneCard
                key={scene.id}
                scene={scene}
                sceneIndex={idx}
                sceneNumber={Number(scene.segment_number || scene.scene_number) || idx + 1}
                projectId={isNarrationMode ? projectId : null}
                characterById={characterById}
                isNarrationMode={isNarrationMode}
                narrationVideoProvider={story?._narrationVideoProvider}
                defaultVideoEngine={story?.default_scene_video_provider || 'nano_banana'}
                isEditing={editingScene === scene.id}
                isGeneratingImage={generatingImages[scene.id]}
                isGeneratingVideo={generatingVideos[scene.id]}
                isSavingCameraDirection={savingCameraDirection[scene.id]}
                isSavingImagePrompt={savingImagePrompt[scene.id]}
                isLipSyncingVideo={lipSyncingVideos[scene.id]}
                isExpandingImage={expandingImages[scene.id]}
                isGeneratingSceneSfx={generatingSceneSfx[scene.id]}
                onStartEdit={() => setEditingScene(scene.id)}
                onSaveEdit={(text) => updateSceneText(scene.id, text)}
                onCancelEdit={() => setEditingScene(null)}
                onRequestGenerateImage={() => openSingleGenDialog('image', scene.id)}
                onRequestGenerateVideo={(cameraMotion) => openSingleGenDialog('video', scene.id, cameraMotion)}
                onSaveCameraDirection={(value) => saveSceneCameraDirection(scene.id, value)}
                onSaveImagePrompt={(value) => saveSceneImagePrompt(scene.id, value)}
                onLipSyncVideo={(provider, cameraMotion) => lipSyncSceneVideo(scene.id, provider, cameraMotion)}
                onExpandImage={(aspectRatio) => expandSceneImage(scene.id, aspectRatio)}
                onGenerateSceneSfx={() => generateSceneSoundEffect(scene.id)}
                onSelectMedia={(kind, url) => selectSceneMedia(scene.id, kind, url)}
                onUploadImage={(file) => uploadMedia(scene.id, file, 'image')}
                onUploadVideo={(file) => uploadMedia(scene.id, file, 'video')}
                onOpenDoodle={() => setDoodleModalScene(scene)}
                onOpenSceneAudio={() => setSceneAudioTarget(scene)}
                onOpenDialogueCamera={isDialogueStory ? () => setDialogueCameraSceneId(scene.id) : null}
                isGeneratingSceneAudio={sceneAudioGeneratingId === scene.id}
                isAdaptingScriptToVideo={adaptingScriptSceneId === scene.id}
                adaptScriptRegenerateAudio={adaptScriptRegenerateAudio}
                onAdaptScriptToVideo={() => adaptSceneScriptToVideoDuration(scene, {
                  regenerateAudio: adaptScriptRegenerateAudio,
                })}
                isSelected={selectedSceneIds.has(scene.id)}
                onToggleSelect={() => toggleSceneSelection(scene.id)}
                onDelete={() => requestDeleteScenes([scene.id])}
                deleteDisabled={deletingScenes || scenes.length <= 1}
                isSongProject={isSongProject}
                multishotSelectionSaving={multishotSelectionSavingId === scene.id}
                onMultishotMasterToggle={(enabled) => handleMultishotMasterToggle(scene.id, enabled)}
                onMultishotShotToggle={(shotIndex, enabled) => handleMultishotShotToggle(scene.id, shotIndex, enabled)}
              />
            ))}
          </div>
          {isNarrationMode && (scenesHasMore || scenesLoadingMore) ? (
            <div
              ref={loadMoreSentinelRef}
              className="flex items-center justify-center gap-2 py-8 text-sm"
              style={{ color: 'var(--text-tertiary)' }}
              data-testid="scene-edit-load-more"
            >
              {scenesLoadingMore ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-[#62FAE3]" />
                  Loading more scenes…
                </>
              ) : (
                <span>
                  Showing {scenes.length}
                  {scenesTotal ? ` of ${scenesTotal}` : ''} scenes — scroll for more
                </span>
              )}
            </div>
          ) : null}
          {isNarrationMode && !scenesHasMore && scenesTotal > 0 && scenes.length > 0 ? (
            <p
              className="text-center text-xs py-4"
              style={{ color: 'var(--text-tertiary)' }}
              data-testid="scene-edit-all-loaded"
            >
              {scenes.length} scene{scenes.length === 1 ? '' : 's'} loaded
            </p>
          ) : null}
        </motion.div>
      </div>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={(open) => {
        if (!deletingScenes) {
          setDeleteConfirmOpen(open);
          if (!open) setPendingDeleteIds([]);
        }
      }}>
        <AlertDialogContent
          className="rounded-2xl"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: 'var(--text-primary)' }}>
              {pendingDeleteIds.length === 1 ? 'Delete scene?' : `Delete ${pendingDeleteIds.length} scenes?`}
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'var(--text-secondary)' }}>
              {pendingDeleteIds.length === 1
                ? 'This scene will be removed permanently. Remaining scenes will be renumbered.'
                : `${pendingDeleteIds.length} selected scenes will be removed. Remaining scenes will be renumbered.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingScenes} className="rounded-lg">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deletingScenes}
              className="rounded-lg"
              style={{ background: '#ef4444', color: 'white' }}
              onClick={(e) => {
                e.preventDefault();
                confirmDeleteScenes();
              }}
              data-testid="scene-delete-confirm-btn"
            >
              {deletingScenes ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {movieDirectorOpen ? (
        <MovieDirectorPanel
          layout="docked"
          dockedBackdrop={false}
          story={story}
          scenes={scenes}
          editorMode={isNarrationMode ? 'narration' : 'story'}
          activeSceneNumber={directorFocusScene.number}
          activeSceneId={directorFocusScene.id}
          onHide={() => setMovieDirectorOpen(false)}
        />
      ) : null}

      <StorySoundPanel
        storyId={storyId}
        open={soundPanelOpen}
        payload={storySound.payload}
      />

      {/* Doodle Canvas Modal */}
      {doodleModalScene && !isNarrationMode && (
        <DoodleCanvasModal
          isOpen={true}
          onClose={() => setDoodleModalScene(null)}
          sceneId={doodleModalScene.id}
          sceneTitle={doodleModalScene.scene_title || doodleModalScene.title}
          onImageGenerated={handleDoodleImageGenerated}
        />
      )}

      <Dialog open={addSceneOpen} onOpenChange={setAddSceneOpen}>
        <DialogContent
          className="max-w-md rounded-2xl"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
        >
          <DialogHeader>
            <DialogTitle className="text-base" style={{ color: 'var(--text-primary)' }}>
              Add scene
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
              Choose where the new scene appears in the story. Scene 1 is the opening; higher numbers are later.
              Order is saved to the server when you confirm.
            </p>
            <div className="space-y-1.5">
              <label className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                Position in story
              </label>
              <select
                className="w-full rounded-lg border px-3 py-2 text-sm bg-transparent"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                value={addScenePlaceAs}
                onChange={(e) => setAddScenePlaceAs(Number(e.target.value))}
                data-testid="add-scene-position-select"
              >
                {Array.from({ length: Math.max(1, scenes.length + 1) }, (_, i) => i + 1).map((k) => {
                  const last = scenes.length + 1;
                  let label;
                  if (k === 1) label = 'As scene 1 (start)';
                  else if (k === last) label = `As scene ${k} (after current last)`;
                  else label = `As scene ${k} (later scenes shift down)`;
                  return (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                Title (optional)
              </label>
              <input
                type="text"
                value={addSceneTitle}
                onChange={(e) => setAddSceneTitle(e.target.value)}
                placeholder="New scene"
                className="w-full rounded-lg border px-3 py-2 text-sm bg-transparent"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                data-testid="add-scene-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                Scene description (optional)
              </label>
              <Textarea
                value={addSceneText}
                onChange={(e) => setAddSceneText(e.target.value)}
                placeholder="What happens in this beat…"
                rows={3}
                className="rounded-lg text-sm resize-y"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                data-testid="add-scene-text-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-lg"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                onClick={() => setAddSceneOpen(false)}
                disabled={addSceneSaving}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                className="rounded-lg"
                style={{ background: 'var(--primary)', color: 'white' }}
                onClick={handleAddSceneSave}
                disabled={addSceneSaving}
                data-testid="add-scene-save-btn"
              >
                {addSceneSaving ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin inline" /> Saving…
                  </>
                ) : (
                  <>
                    <Save className="w-3.5 h-3.5 mr-1.5 inline" /> Add and save order
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {isNarrationMode ? (
      <>
      <Dialog open={extendStoryOpen} onOpenChange={(open) => {
        if (!extendCommitting) setExtendStoryOpen(open);
      }}>
        <DialogContent
          className="max-w-5xl max-h-[92vh] overflow-y-auto rounded-2xl"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
        >
          <DialogHeader>
            <DialogTitle className="text-base" style={{ color: 'var(--text-primary)' }}>
              Extend story for next episode
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Choose where the story continues from. Use AI to propose two continuations, or describe your own idea and we will draft scenes with style-matched image prompts. Edit each scene, then approve to add them and generate images.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <motion.div className="space-y-2">
              <Label className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                Script source
              </Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={extendScriptSource === 'ai' ? 'default' : 'outline'}
                  className="rounded-lg h-8 gap-1.5"
                  onClick={() => {
                    setExtendScriptSource('ai');
                    setExtendOptions([]);
                    setSelectedExtendOptionId('');
                  }}
                  disabled={extendProposing || extendCommitting}
                  data-testid="extend-script-source-ai"
                >
                  <TwinneeLogo className="w-3.5 h-3.5" />
                  AI generate
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={extendScriptSource === 'manual' ? 'default' : 'outline'}
                  className="rounded-lg h-8 gap-1.5"
                  onClick={() => {
                    setExtendScriptSource('manual');
                    setExtendOptions([]);
                    setSelectedExtendOptionId('');
                  }}
                  disabled={extendProposing || extendCommitting}
                  data-testid="extend-script-source-manual"
                >
                  <FileText className="w-3.5 h-3.5" />
                  Write script manually
                </Button>
              </div>
            </motion.div>
            {extendScriptSource === 'ai' ? (
            <motion.div key="extend-ai-flow" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  Continue story from scene
                </Label>
                <select
                  className="w-full rounded-lg border px-3 py-2 text-sm bg-transparent h-9"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  value={extendStartFromScene}
                  onChange={(e) => setExtendStartFromScene(Number(e.target.value))}
                  disabled={extendProposing || extendCommitting || scenes.length === 0}
                  data-testid="extend-start-scene-select"
                >
                  {scenes.length === 0 ? (
                    <option value={1}>Scene 1 (start)</option>
                  ) : (
                    scenes.map((scene, idx) => {
                      const num = Number(scene.segment_number || scene.scene_number) || idx + 1;
                      const preview = String(scene.scene_text || scene.text || '').trim().slice(0, 72);
                      const suffix = preview ? `: ${preview}${preview.length >= 72 ? '…' : ''}` : '';
                      const afterNote = num < lastSceneNumber ? ' — later scenes will shift' : '';
                      return (
                        <option key={scene.id} value={num}>
                          {`Scene ${num}${suffix}${afterNote}`}
                        </option>
                      );
                    })
                  )}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  How much to generate
                </Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={extendLengthMode === 'minutes' ? 'default' : 'outline'}
                    className="rounded-lg h-8"
                    onClick={() => setExtendLengthMode('minutes')}
                    disabled={extendProposing || extendCommitting}
                    data-testid="extend-length-mode-minutes"
                  >
                    Minutes
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={extendLengthMode === 'scenes' ? 'default' : 'outline'}
                    className="rounded-lg h-8"
                    onClick={() => setExtendLengthMode('scenes')}
                    disabled={extendProposing || extendCommitting}
                    data-testid="extend-length-mode-scenes"
                  >
                    Number of scenes
                  </Button>
                </div>
                {extendLengthMode === 'minutes' ? (
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    step={0.5}
                    value={extensionMinutes}
                    onChange={(e) => setExtensionMinutes(e.target.value)}
                    className="w-full h-9"
                    disabled={extendProposing || extendCommitting}
                    data-testid="extension-minutes-input"
                  />
                ) : (
                  <Input
                    type="number"
                    min={3}
                    max={28}
                    step={1}
                    value={extensionSceneCount}
                    onChange={(e) => setExtensionSceneCount(e.target.value)}
                    className="w-full h-9"
                    disabled={extendProposing || extendCommitting}
                    data-testid="extension-scene-count-input"
                  />
                )}
              </div>
            </div>
            <Button
              type="button"
              variant="default"
              onClick={proposeStoryExtension}
              disabled={extendProposing || extendCommitting}
              className="rounded-lg gap-2"
              data-testid="propose-extension-options-btn"
            >
              {extendProposing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TwinneeLogo className="w-4 h-4" />}
              {extendProposing ? 'Generating options…' : 'Generate 2 options'}
            </Button>
            </motion.div>
            ) : (
            <motion.div key="extend-manual-flow" className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  Continue story from scene
                </Label>
                <select
                  className="w-full rounded-lg border px-3 py-2 text-sm bg-transparent h-9"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  value={extendStartFromScene}
                  onChange={(e) => setExtendStartFromScene(Number(e.target.value))}
                  disabled={extendProposing || extendCommitting || scenes.length === 0}
                  data-testid="extend-start-scene-select"
                >
                  {scenes.length === 0 ? (
                    <option value={1}>Scene 1 (start)</option>
                  ) : (
                    scenes.map((scene, idx) => {
                      const num = Number(scene.segment_number || scene.scene_number) || idx + 1;
                      const preview = String(scene.scene_text || scene.text || '').trim().slice(0, 72);
                      const suffix = preview ? `: ${preview}${preview.length >= 72 ? '…' : ''}` : '';
                      const afterNote = num < lastSceneNumber ? ' — later scenes will shift' : '';
                      return (
                        <option key={scene.id} value={num}>
                          {`Scene ${num}${suffix}${afterNote}`}
                        </option>
                      );
                    })
                  )}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  Target length for new scenes
                </Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={extendLengthMode === 'minutes' ? 'default' : 'outline'}
                    className="rounded-lg h-8"
                    onClick={() => setExtendLengthMode('minutes')}
                    disabled={extendProposing || extendCommitting}
                    data-testid="extend-length-mode-minutes"
                  >
                    Minutes
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={extendLengthMode === 'scenes' ? 'default' : 'outline'}
                    className="rounded-lg h-8"
                    onClick={() => setExtendLengthMode('scenes')}
                    disabled={extendProposing || extendCommitting}
                    data-testid="extend-length-mode-scenes"
                  >
                    Number of scenes
                  </Button>
                </div>
                {extendLengthMode === 'minutes' ? (
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    step={0.5}
                    value={extensionMinutes}
                    onChange={(e) => setExtensionMinutes(e.target.value)}
                    className="w-full h-9 max-w-xs"
                    disabled={extendProposing || extendCommitting}
                    data-testid="extension-minutes-input"
                  />
                ) : (
                  <Input
                    type="number"
                    min={3}
                    max={28}
                    step={1}
                    value={extensionSceneCount}
                    onChange={(e) => setExtensionSceneCount(e.target.value)}
                    className="w-full h-9 max-w-xs"
                    disabled={extendProposing || extendCommitting}
                    data-testid="extension-scene-count-input"
                  />
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  Your extension story idea
                </Label>
                <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                  Describe what happens next — plot, tone, and key beats. We will draft narration lines and image prompts using your project&apos;s existing visual style from storyboard frames.
                </p>
                <Textarea
                  value={extendStoryIdea}
                  onChange={(e) => setExtendStoryIdea(e.target.value)}
                  rows={8}
                  className="text-sm resize-y min-h-[10rem]"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  placeholder="Example: After the storm, Maya finds an old map in the lighthouse basement. She follows it to a hidden cove where a stranger has been watching the village. The episode ends as she recognizes the stranger's face..."
                  disabled={extendProposing || extendCommitting}
                  data-testid="extend-story-idea-input"
                />
              </div>
              <Button
                type="button"
                variant="default"
                onClick={proposeStoryExtension}
                disabled={extendProposing || extendCommitting}
                className="rounded-lg gap-2"
                data-testid="propose-extension-from-idea-btn"
              >
                {extendProposing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                {extendProposing ? 'Generating scenes…' : 'Generate scenes from your idea'}
              </Button>
            </motion.div>
            )}

            {extendReferenceVisualStyle ? (
              <div
                className="rounded-xl border p-3 space-y-2"
                style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' }}
              >
                <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
                  Unified image style (from {extendStyleProfiles.length || 'existing'} storyboard frame{(extendStyleProfiles.length === 1) ? '' : 's'})
                </p>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                  {extendReferenceVisualStyle}
                </p>
                {extendStyleProfiles.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {extendStyleProfiles.map((p) => (
                      <span
                        key={`style-${p.segment_number}-${p.medium}`}
                        className="text-[10px] px-2 py-0.5 rounded-full"
                        style={{ background: 'var(--primary-light)', color: 'var(--text-secondary)' }}
                      >
                        Seg {p.segment_number}: {p.medium || 'style'}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            {extendOptions.length > 0 ? (
              <motion.div className={`grid gap-4 ${extendOptions.length > 1 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
                {extendOptions.map((opt) => {
                  const isManualOption = opt.id === 'manual' || extendScriptSource === 'manual';
                  const selected = isManualOption || selectedExtendOptionId === opt.id;
                  const segments = Array.isArray(opt.segments) ? opt.segments : [];
                  return (
                    <motion.div
                      key={opt.id}
                      role={isManualOption ? undefined : 'button'}
                      tabIndex={isManualOption ? undefined : 0}
                      onClick={isManualOption ? undefined : () => setSelectedExtendOptionId(opt.id)}
                      onKeyDown={isManualOption ? undefined : (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedExtendOptionId(opt.id);
                        }
                      }}
                      className={`text-left rounded-xl border-2 p-3 transition-all ${
                        isManualOption ? '' : 'cursor-pointer '
                      }${
                        selected ? 'border-primary bg-[var(--primary-light)] ring-2 ring-primary/40' : 'border-[var(--glass-border)] bg-[var(--bg-tertiary)]'
                      }`}
                      data-testid={`extend-option-${opt.id}`}
                    >
                      <motion.div className="flex items-start justify-between gap-2 mb-2">
                        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {opt.title || opt.id}
                        </p>
                        {selected ? (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0" style={{ background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}>
                            {isManualOption ? 'Review & edit' : 'Selected'}
                          </span>
                        ) : null}
                      </motion.div>
                      {opt.id !== 'manual' && opt.hook_summary ? (
                        <p className="text-[11px] mb-2" style={{ color: 'var(--text-secondary)' }}>
                          <span className="font-medium" style={{ color: 'var(--text-tertiary)' }}>Hook: </span>
                          {opt.hook_summary}
                        </p>
                      ) : null}
                      {opt.id !== 'manual' && opt.ending_teaser ? (
                        <>
                          <p className="text-[10px] font-medium uppercase tracking-wide mb-1" style={{ color: 'var(--text-tertiary)' }}>
                            Cliffhanger ending
                          </p>
                          <p className="text-xs italic mb-3" style={{ color: 'var(--text-primary)' }}>
                            &ldquo;{opt.ending_teaser}&rdquo;
                          </p>
                        </>
                      ) : null}
                      <p className="text-[10px] font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-tertiary)' }}>
                        Script — {segments.length} scene{segments.length === 1 ? '' : 's'}
                      </p>
                      <div
                        className="space-y-3 max-h-[min(52vh,28rem)] overflow-y-auto pr-1 rounded-lg border p-2"
                        style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {segments.map((seg, idx) => {
                          const sceneNum = Number(seg.segment_number) || idx + 1;
                          const scriptText = seg.text || seg.scene_text || '';
                          const imagePrompt = seg.visual || seg.camera_direction || '';
                          return (
                            <motion.div
                              key={`${opt.id}-seg-${idx}`}
                              className="space-y-2 border-b pb-3 last:border-0 last:pb-0"
                              style={{ borderColor: 'var(--glass-border)' }}
                            >
                              <p className="text-[11px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                                Scene {sceneNum}
                                {seg.emotion ? (
                                  <span className="font-normal ml-1.5 capitalize" style={{ color: 'var(--text-tertiary)' }}>
                                    · {seg.emotion}
                                  </span>
                                ) : null}
                                {seg.duration != null ? (
                                  <span className="font-normal ml-1" style={{ color: 'var(--text-tertiary)' }}>
                                    · ~{Number(seg.duration).toFixed(1)}s
                                  </span>
                                ) : null}
                              </p>
                              <div className="space-y-1">
                                <Label className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                                  Narration script
                                </Label>
                                <Textarea
                                  value={scriptText}
                                  onChange={(e) => updateExtendOptionSegment(opt.id, idx, 'text', e.target.value)}
                                  rows={3}
                                  className="text-xs resize-y min-h-[4.5rem]"
                                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                                  disabled={extendCommitting}
                                  data-testid={`extend-option-${opt.id}-scene-${idx}-script`}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                                  Image prompt
                                </Label>
                                <Textarea
                                  value={imagePrompt}
                                  onChange={(e) => updateExtendOptionSegment(opt.id, idx, 'visual', e.target.value)}
                                  rows={3}
                                  className="text-xs resize-y min-h-[4.5rem]"
                                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                                  disabled={extendCommitting}
                                  data-testid={`extend-option-${opt.id}-scene-${idx}-visual`}
                                />
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            ) : extendProposing ? (
              <p className="text-xs py-6 text-center flex items-center justify-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                <Loader2 className="w-4 h-4 animate-spin" />
                {extendScriptSource === 'manual'
                  ? 'Drafting scenes and style-matched image prompts from your idea…'
                  : 'Sampling storyboard styles and writing two continuation scripts…'}
              </p>
            ) : (
              <p className="text-xs py-6 text-center" style={{ color: 'var(--text-tertiary)' }}>
                {extendScriptSource === 'manual'
                  ? 'Choose a starting scene, set length, describe your idea, then click Generate scenes from your idea.'
                  : 'Pick a starting scene and length, then generate two continuation scripts to choose from.'}
              </p>
            )}

            {extendCommitMessage ? (
              <p className="text-xs flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {extendCommitMessage}
              </p>
            ) : null}

            <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: 'var(--glass-border)' }}>
              <Button
                type="button"
                variant="outline"
                onClick={() => setExtendStoryOpen(false)}
                disabled={extendCommitting}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="default"
                onClick={commitStoryExtension}
                disabled={extendCommitting || !selectedExtendOption || extendOptions.length === 0}
                className="rounded-lg gap-2"
                data-testid="approve-extension-option-btn"
              >
                {extendCommitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Approve &amp; generate scene images
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkAudioDialogOpen} onOpenChange={(open) => {
        if (!bulkAudioGenerating) setBulkAudioDialogOpen(open);
      }}>
        <DialogContent
          className="max-w-md rounded-2xl"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
        >
          <DialogHeader>
            <DialogTitle className="text-base" style={{ color: 'var(--text-primary)' }}>
              Generate narration audio
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Choose audio provider and voice, then pick which segments to generate.
            </DialogDescription>
          </DialogHeader>
          {isNarrationMode ? (
            <NarrationAudioSettingsPanel
              settings={narrationAudioSettings}
              onChange={onNarrationAudioSettingsChange}
              onChooseVoice={() => setNarrationVoicePickerOpen(true)}
              disabled={bulkAudioGenerating}
              testId="narration-bulk-audio-settings"
            />
          ) : null}
          <div className="grid gap-3 py-2">
            <button
              type="button"
              disabled={pendingNarrationAudioCount === 0}
              onClick={() => handleBulkNarrationAudioChoice('pending')}
              className="rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/60"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              data-testid="narration-bulk-audio-pending-btn"
            >
              <span className="block text-sm font-semibold">Pending only</span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {pendingNarrationAudioCount
                  ? `${pendingNarrationAudioCount} segment${pendingNarrationAudioCount === 1 ? '' : 's'} missing audio`
                  : 'All segments already have audio'}
              </span>
            </button>
            <button
              type="button"
              disabled={allNarrationAudioCount === 0}
              onClick={() => handleBulkNarrationAudioChoice('all')}
              className="rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/60"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              data-testid="narration-bulk-audio-all-btn"
            >
              <span className="block text-sm font-semibold">All scenes</span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {allNarrationAudioCount
                  ? `Regenerate audio for all ${allNarrationAudioCount} segment${allNarrationAudioCount === 1 ? '' : 's'} with script`
                  : 'No segments with script text'}
              </span>
            </button>
            <button
              type="button"
              disabled={selectedNarrationAudioCount === 0}
              onClick={() => handleBulkNarrationAudioChoice('selected')}
              className="rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/60"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              data-testid="narration-bulk-audio-selected-btn"
            >
              <span className="block text-sm font-semibold">Selected scenes</span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {selectedNarrationAudioCount
                  ? `Generate audio for ${selectedNarrationAudioCount} selected segment${selectedNarrationAudioCount === 1 ? '' : 's'}`
                  : 'Select scenes in the grid below first'}
              </span>
            </button>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkAudioDialogOpen(false)}
              disabled={bulkAudioGenerating}
              className="rounded-lg"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkAdaptScriptDialogOpen} onOpenChange={(open) => {
        if (!bulkAdaptScriptGenerating) setBulkAdaptScriptDialogOpen(open);
      }}>
        <DialogContent
          className="max-w-md rounded-2xl"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
        >
          <DialogHeader>
            <DialogTitle className="text-base" style={{ color: 'var(--text-primary)' }}>
              Match script to video length
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              AI rewrites each segment&apos;s narration in continuous storytelling style (picking up from prior
              scenes) so spoken audio fits the full source video file (~2.5 words/sec), not the trimmed timeline length.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 cursor-pointer text-xs leading-snug px-1"
            style={{ color: 'var(--text-secondary)' }}>
            <input
              type="checkbox"
              checked={adaptScriptRegenerateAudio}
              onChange={(e) => setAdaptScriptRegenerateAudio(e.target.checked)}
              className="rounded mt-0.5 shrink-0"
              style={{ accentColor: 'var(--primary)' }}
            />
            <span>Also regenerate narration audio after adapting each script</span>
          </label>
          <div className="grid gap-3 py-2">
            <button
              type="button"
              disabled={selectedNarrationAdaptScriptCount === 0}
              onClick={() => handleBulkAdaptScriptChoice('selected')}
              className="rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/60"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              data-testid="narration-bulk-adapt-script-selected-btn"
            >
              <span className="block text-sm font-semibold">Selected scenes</span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {selectedNarrationAdaptScriptCount
                  ? `${selectedNarrationAdaptScriptCount} selected segment${selectedNarrationAdaptScriptCount === 1 ? '' : 's'} with video`
                  : 'Select scenes that have video and script text'}
              </span>
            </button>
            <button
              type="button"
              disabled={allNarrationAdaptScriptCount === 0}
              onClick={() => handleBulkAdaptScriptChoice('all')}
              className="rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/60"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              data-testid="narration-bulk-adapt-script-all-btn"
            >
              <span className="block text-sm font-semibold">All scenes with video</span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {allNarrationAdaptScriptCount
                  ? `Adapt ${allNarrationAdaptScriptCount} segment${allNarrationAdaptScriptCount === 1 ? '' : 's'}`
                  : 'No segments with both script and video yet'}
              </span>
            </button>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkAdaptScriptDialogOpen(false)}
              disabled={bulkAdaptScriptGenerating}
              className="rounded-lg"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkImageDialogOpen} onOpenChange={(open) => {
        if (!bulkImageGenerating) setBulkImageDialogOpen(open);
      }}>
        <DialogContent
          className="max-w-md rounded-2xl"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
        >
          <DialogHeader>
            <DialogTitle className="text-base" style={{ color: 'var(--text-primary)' }}>
              Generate scene images
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Choose an image provider, then pending scenes without images, all scenes with prompts, or only selected scenes.
            </DialogDescription>
          </DialogHeader>
          <motion.div className="space-y-2 px-1">
            <Label className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Image provider</Label>
            <Select
              value={narrationImageProvider}
              onValueChange={setNarrationImageProvider}
              disabled={bulkImageGenerating}
            >
              <SelectTrigger
                className="rounded-lg h-10 text-sm"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                data-testid="narration-bulk-image-provider-select"
              >
                <SelectValue placeholder="Image provider" />
              </SelectTrigger>
              <SelectContent>
                {NARRATION_IMAGE_PROVIDER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </motion.div>
          <div className="grid gap-3 py-2">
            <button
              type="button"
              disabled={pendingNarrationImageCount === 0}
              onClick={() => handleBulkNarrationImageChoice('pending')}
              className="rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/60"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              data-testid="narration-bulk-image-pending-btn"
            >
              <span className="block text-sm font-semibold">Pending only</span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {pendingNarrationImageCount
                  ? `${pendingNarrationImageCount} scene${pendingNarrationImageCount === 1 ? '' : 's'} missing images`
                  : 'All scenes already have images'}
              </span>
            </button>
            <button
              type="button"
              disabled={allNarrationImageCount === 0}
              onClick={() => handleBulkNarrationImageChoice('all')}
              className="rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/60"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              data-testid="narration-bulk-image-all-btn"
            >
              <span className="block text-sm font-semibold">All scenes</span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {allNarrationImageCount
                  ? `Generate or regenerate images for all ${allNarrationImageCount} scene${allNarrationImageCount === 1 ? '' : 's'} with prompts`
                  : 'No scenes with script or image prompts'}
              </span>
            </button>
            <button
              type="button"
              disabled={selectedNarrationImageCount === 0}
              onClick={() => handleBulkNarrationImageChoice('selected')}
              className="rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/60"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              data-testid="narration-bulk-image-selected-btn"
            >
              <span className="block text-sm font-semibold">Selected scenes</span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {selectedNarrationImageCount
                  ? `Generate images for ${selectedNarrationImageCount} selected scene${selectedNarrationImageCount === 1 ? '' : 's'}`
                  : 'Select scenes in the grid below first'}
              </span>
            </button>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkImageDialogOpen(false)}
              disabled={bulkImageGenerating}
              className="rounded-lg"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkMultishotDialogOpen} onOpenChange={(open) => {
        if (!bulkMultishotGenerating) setBulkMultishotDialogOpen(open);
      }}>
        <DialogContent
          className="max-w-md rounded-2xl"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
        >
          <DialogHeader>
            <DialogTitle className="text-base" style={{ color: 'var(--text-primary)' }}>
              Generate multishots
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Queue alternate camera angles per scene on the worker. The video editor timeline can split
              each scene equally between the master image and the new shots across its audio length.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {isNarrationMode ? (
              <div className="space-y-2">
                <Label className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Image provider
                </Label>
                <Select
                  value={narrationImageProvider || story?.image_provider || 'nano_banana'}
                  onValueChange={setNarrationImageProvider}
                  disabled={bulkMultishotGenerating}
                >
                  <SelectTrigger className="rounded-lg" data-testid="multishot-image-provider-select">
                    <SelectValue placeholder="Image provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {NARRATION_IMAGE_PROVIDER_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="space-y-2">
              <Label className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                Multishots per scene (N)
              </Label>
              <Input
                type="number"
                min={1}
                max={8}
                value={multishotCount}
                onChange={(e) => setMultishotCount(e.target.value)}
                disabled={bulkMultishotGenerating}
                data-testid="multishot-count-input"
              />
              <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                Timeline uses N + 1 equal parts (master + N alternates).
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                Prompt per alternate shot
              </Label>
              <Textarea
                value={multishotPromptTemplate}
                onChange={(e) => setMultishotPromptTemplate(e.target.value)}
                disabled={bulkMultishotGenerating}
                rows={5}
                className="rounded-lg text-sm resize-y min-h-[100px]"
                placeholder={DEFAULT_MULTISHOT_PROMPT_TEMPLATE}
                data-testid="multishot-prompt-template"
              />
              <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                Use {'{index}'} for the shot number (1, 2, …). Appended to each scene&apos;s visual prompt.
              </p>
            </div>
            <button
              type="button"
              disabled={selectedSceneCount === 0 || bulkMultishotGenerating}
              onClick={() => handleBulkMultishotChoice('selected')}
              className="rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/60 w-full"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              data-testid="multishot-selected-btn"
            >
              <span className="block text-sm font-semibold">Queue selected scenes</span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {selectedSceneCount
                  ? `${selectedSceneCount} selected scene${selectedSceneCount === 1 ? '' : 's'} with images`
                  : 'Select one or more scenes on the grid first'}
              </span>
            </button>
            <button
              type="button"
              disabled={multishotTargets.withImage.length === 0 || bulkMultishotGenerating}
              onClick={() => handleBulkMultishotChoice('all')}
              className="rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/60 w-full"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              data-testid="multishot-all-btn"
            >
              <span className="block text-sm font-semibold">Queue all scenes with images</span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {multishotTargets.withImage.length
                  ? `${multishotTargets.withImage.length} scene${multishotTargets.withImage.length === 1 ? '' : 's'}`
                  : 'No scenes have master images yet'}
              </span>
            </button>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setBulkMultishotDialogOpen(false)}
              disabled={bulkMultishotGenerating}
              className="rounded-lg"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </>
      ) : null}

      <Dialog open={bulkVideoDialogOpen} onOpenChange={(open) => {
        if (!bulkVideoGenerating) setBulkVideoDialogOpen(open);
      }}>
        <DialogContent
          className="max-w-md rounded-2xl"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
        >
          <DialogHeader>
            <DialogTitle className="text-base" style={{ color: 'var(--text-primary)' }}>
              Generate videos
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {isNarrationMode
                ? 'Choose a video provider, then generate for pending segments (image + audio, no video), all ready segments, or selected only.'
                : 'Choose a video provider, then generate for scenes missing video, all scenes with images, or selected only.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 px-1">
            <Label className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Video provider</Label>
            <Select
              value={isNarrationMode ? narrationVideoProvider : batchVideoProvider}
              onValueChange={isNarrationMode ? setNarrationVideoProvider : setBatchVideoProvider}
              disabled={bulkVideoGenerating}
            >
              <SelectTrigger
                className="rounded-lg h-10 text-sm"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                data-testid="bulk-video-provider-select"
              >
                <SelectValue placeholder="Video provider" />
              </SelectTrigger>
              <SelectContent>
                {videoProviderOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-3 py-2">
            <button
              type="button"
              disabled={pendingVideoCount === 0}
              onClick={() => handleBulkVideoChoice('pending')}
              className="rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/60"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              data-testid="bulk-video-pending-btn"
            >
              <span className="block text-sm font-semibold">Pending only</span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {pendingVideoCount
                  ? `${pendingVideoCount} scene${pendingVideoCount === 1 ? '' : 's'} ready for video`
                  : isNarrationMode
                    ? 'All segments already have video (or need image + audio first)'
                    : 'All scenes already have video (or need images first)'}
              </span>
            </button>
            <button
              type="button"
              disabled={allVideoCount === 0}
              onClick={() => handleBulkVideoChoice('all')}
              className="rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/60"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              data-testid="bulk-video-all-btn"
            >
              <span className="block text-sm font-semibold">All scenes</span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {allVideoCount
                  ? `Queue video for all ${allVideoCount} scene${allVideoCount === 1 ? '' : 's'} with${isNarrationMode ? ' image + audio' : ' images'}`
                  : 'No eligible scenes yet'}
              </span>
            </button>
            <button
              type="button"
              disabled={selectedVideoCount === 0}
              onClick={() => handleBulkVideoChoice('selected')}
              className="rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/60"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              data-testid="bulk-video-selected-btn"
            >
              <span className="block text-sm font-semibold">Selected scenes</span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {selectedVideoCount
                  ? `Generate video for ${selectedVideoCount} selected scene${selectedVideoCount === 1 ? '' : 's'}`
                  : 'Select scenes in the grid below first'}
              </span>
            </button>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkVideoDialogOpen(false)}
              disabled={bulkVideoGenerating}
              className="rounded-lg"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(singleGenDialog)} onOpenChange={(open) => { if (!open) setSingleGenDialog(null); }}>
        <DialogContent
          className="max-w-sm rounded-2xl"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
        >
          <DialogHeader>
            <DialogTitle className="text-base" style={{ color: 'var(--text-primary)' }}>
              {singleGenDialog?.kind === 'image' ? 'Generate image' : 'Generate video'}
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {singleGenDialog?.kind === 'image'
                ? 'Choose which image model to use for this scene.'
                : 'Choose which video engine to use. Camera motion from the scene card is applied when supported.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
              {singleGenDialog?.kind === 'image' ? 'Image provider' : 'Video provider'}
            </Label>
            <Select value={singleGenProvider} onValueChange={setSingleGenProvider}>
              <SelectTrigger
                className="rounded-lg h-10 text-sm"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                data-testid="single-scene-gen-provider-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(singleGenDialog?.kind === 'image'
                  ? (isNarrationMode ? NARRATION_IMAGE_PROVIDER_OPTIONS : NARRATION_IMAGE_PROVIDER_OPTIONS)
                  : videoProviderOptions
                ).map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setSingleGenDialog(null)}
              className="rounded-lg"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmSingleGenDialog}
              className="rounded-lg"
              style={{
                background: singleGenDialog?.kind === 'image'
                  ? 'hsl(var(--primary))'
                  : '#f97316',
                color: singleGenDialog?.kind === 'image'
                  ? 'hsl(var(--primary-foreground))'
                  : '#fff',
              }}
              data-testid="single-scene-gen-confirm-btn"
            >
              {singleGenDialog?.kind === 'image' ? 'Generate image' : 'Generate video'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VideoGenerationPromptDialog
        open={Boolean(videoPromptReview)}
        onOpenChange={(open) => {
          if (!open) {
            setVideoPromptReview(null);
            setVideoCreditEstimate(null);
          }
        }}
        provider={videoPromptReview?.provider}
        initialPrompt={videoPromptReview?.prompt || ''}
        maxLength={videoPromptReview?.maxLength || 2000}
        loading={videoPromptReviewLoading}
        creditEstimate={videoCreditEstimate}
        onConfirm={confirmVideoPromptReview}
      />

      <Dialog open={storyAudioPanelOpen} onOpenChange={setStoryAudioPanelOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl" style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}>
          <DialogHeader>
            <DialogTitle className="text-base" style={{ color: 'var(--text-primary)' }}>
              Story audio settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
                Scene media progress
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { Icon: ImageIcon, cur: scenesWithImages, total: scenes.length, label: 'Images' },
                  { Icon: Video, cur: scenesWithVideos, total: scenes.length, label: 'Videos' },
                  { Icon: Volume2, cur: scenesWithAudio, total: scenes.length, label: 'Audio' },
                ].map(({ Icon, cur, total, label }) => (
                  <div
                    key={label}
                    className="rounded-lg border px-3 py-2"
                    style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' }}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-3.5 h-3.5 shrink-0 opacity-70" style={{ color: 'var(--text-secondary)' }} aria-hidden />
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{label}</span>
                    </div>
                    <p className="mt-1 text-sm font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>
                      {cur}/{total} {label.toLowerCase()}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {isNarrationMode ? (
              <NarrationAudioSettingsBar
                settings={narrationAudioSettings}
                onChange={onNarrationAudioSettingsChange}
                murfVoices={murfVoicesCatalog}
                onChooseVoice={() => {
                  if (storyTtsProvider === 'auto') {
                    toast.info('Choose ElevenLabs, Murf, or MiniMax as the audio provider first');
                    return;
                  }
                  setNarrationVoicePickerOpen(true);
                }}
                disabled={storyAudioGenerating || bulkAudioGenerating}
              />
            ) : (
            <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>Language</span>
                <select
                  value={storyAudioLanguage}
                  onChange={(e) => setStoryAudioLanguage(e.target.value)}
                  disabled={storyAudioGenerating}
                  className="h-9 w-full text-xs rounded-lg px-2 border"
                  style={{
                    background: 'var(--bg-tertiary)',
                    borderColor: 'var(--glass-border)',
                    color: 'var(--text-primary)',
                  }}
                  aria-label="Story audio language"
                >
                  <option value="en">English</option>
                  <option value="hi">Hindi</option>
                  <option value="pa">Punjabi</option>
                  <option value="bho">Bhojpuri</option>
                  {!['en', 'hi', 'pa', 'bho'].includes(storyAudioLanguage) ? (
                    <option value={storyAudioLanguage}>{`Story language (${storyAudioLanguage})`}</option>
                  ) : null}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>Narrator style</span>
                <select
                  value={storyAudioVoice}
                  onChange={(e) => setStoryAudioVoice(e.target.value)}
                  disabled={storyAudioGenerating}
                  className="h-9 w-full text-xs rounded-lg px-2 border"
                  style={{
                    background: 'var(--bg-tertiary)',
                    borderColor: 'var(--glass-border)',
                    color: 'var(--text-primary)',
                  }}
                  aria-label="Story narrator voice style"
                >
                  <option value="storyteller">Storyteller</option>
                  <option value="female">Female narrator</option>
                  <option value="male">Male narrator</option>
                  <option value="child">Child narrator</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>
                Narrator MiniMax voice ID (optional)
              </span>
              <input
                type="text"
                value={storyNarratorMinimax}
                onChange={(e) => setStoryNarratorMinimax(e.target.value)}
                disabled={storyAudioGenerating}
                placeholder="Voice ID"
                className="h-9 w-full text-xs rounded-lg px-2 border min-w-0"
                style={{
                  background: 'var(--bg-tertiary)',
                  borderColor: 'var(--glass-border)',
                  color: 'var(--text-primary)',
                }}
                aria-label="Optional MiniMax voice id for narrator"
              />
            </div>

            </>
            )}

            {!isNarrationMode && (story?.characters || []).length > 0 && (
              <div className="space-y-3 pt-1 border-t" style={{ borderColor: 'var(--glass-border)' }}>
                <span className="text-[11px] font-semibold block" style={{ color: 'var(--text-primary)' }}>
                  Character voice IDs (applies to all scenes)
                </span>
                {(story?.characters || []).map((c) => (
                  <div key={c.id} className="rounded-lg p-2 space-y-2" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}>
                    <span className="text-[10px] font-semibold block" style={{ color: 'var(--text-primary)' }}>
                      {c.name || c.id}
                    </span>
                    <select
                      value={modalCharDialogueProvider[c.id] || 'auto'}
                      onChange={(e) =>
                        setModalCharDialogueProvider((prev) => ({ ...prev, [c.id]: e.target.value }))
                      }
                      className="text-xs rounded-lg px-2 py-1.5 border w-full"
                      style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                    >
                      <option value="auto">Auto</option>
                      <option value="murf">Prefer Murf</option>
                      <option value="elevenlabs">Prefer ElevenLabs</option>
                      <option value="minimax">Prefer MiniMax</option>
                      <option value="runway">Runway ML audio</option>
                    </select>
                    {(modalCharDialogueProvider[c.id] || 'auto') === 'runway' && (
                      <select
                        value={modalCharRunwayModel[c.id] || 'eleven_multilingual_v2'}
                        onChange={(e) => setModalCharRunwayModel((prev) => ({ ...prev, [c.id]: e.target.value }))}
                        className="text-xs rounded-lg px-2 py-1.5 border w-full"
                        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                      >
                        {runwayAudioModels.map((m) => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input type="text" value={modalCharMinimax[c.id] || ''} onChange={(e) => setModalCharMinimax((prev) => ({ ...prev, [c.id]: e.target.value }))} placeholder="MiniMax voice_id" className="text-xs rounded-lg px-2 py-1.5 border w-full font-mono"
                        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }} />
                      <input type="text" value={modalCharMurf[c.id] || ''} onChange={(e) => setModalCharMurf((prev) => ({ ...prev, [c.id]: e.target.value }))} placeholder="Murf voiceId" className="text-xs rounded-lg px-2 py-1.5 border w-full font-mono"
                        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }} />
                      <input type="text" value={modalCharMurfLocale[c.id] || ''} onChange={(e) => setModalCharMurfLocale((prev) => ({ ...prev, [c.id]: e.target.value }))} placeholder="Murf locale" className="text-xs rounded-lg px-2 py-1.5 border w-full font-mono"
                        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }} />
                      <input type="text" value={modalCharEleven[c.id] || ''} onChange={(e) => setModalCharEleven((prev) => ({ ...prev, [c.id]: e.target.value }))} placeholder="ElevenLabs voice_id" className="text-xs rounded-lg px-2 py-1.5 border w-full font-mono"
                        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <label
              className="flex items-center gap-2 rounded-lg border px-3 h-9 cursor-pointer"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
            >
              <input
                type="checkbox"
                checked={savePerLineAudioClips}
                onChange={(e) => setSavePerLineAudioClips(e.target.checked)}
                disabled={storyAudioGenerating}
                className="rounded border-slate-300"
              />
              <span className="text-xs font-medium whitespace-nowrap">Save per-line clips</span>
            </label>

            <div className="space-y-3 pt-1 border-t" style={{ borderColor: 'var(--glass-border)' }}>
              <div>
                <span className="text-[11px] font-semibold block" style={{ color: 'var(--text-primary)' }}>
                  Voice dubbing to new story version
                </span>
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                  Dubs existing scene audio into a target language and saves it as a new project version.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>Target language</span>
                  <select
                    value={dubTargetLanguage}
                    onChange={(e) => setDubTargetLanguage(e.target.value)}
                    disabled={storyAudioDubGenerating}
                    className="h-9 w-full text-xs rounded-lg px-2 border"
                    style={{
                      background: 'var(--bg-tertiary)',
                      borderColor: 'var(--glass-border)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {runwayDubLanguages.map((lang) => (
                      <option key={lang.value} value={lang.value}>{lang.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-2 justify-end">
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={dubDisableVoiceCloning}
                      onChange={(e) => setDubDisableVoiceCloning(e.target.checked)}
                      disabled={storyAudioDubGenerating}
                    />
                    <span>Disable voice cloning</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={dubDropBackgroundAudio}
                      onChange={(e) => setDubDropBackgroundAudio(e.target.checked)}
                      disabled={storyAudioDubGenerating}
                    />
                    <span>Drop background audio</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 text-xs px-3 rounded-lg"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  disabled={storyAudioDubGenerating || scenes.length === 0}
                  onClick={createDubbedStoryVersion}
                >
                  {storyAudioDubGenerating ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Dubbing…</>
                  ) : (
                    <><Volume2 className="w-3.5 h-3.5 mr-1.5" /> Dub audio as new version</>
                  )}
                </Button>
              </div>
            </div>

            <div className="flex justify-end pt-1">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 text-xs px-3 rounded-lg"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  disabled={storyAudioGenerating || scenes.length === 0}
                  onClick={generateStoryDialogueAudio}
                  data-testid="generate-story-audio-btn"
                >
                  {storyAudioGenerating ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Audio…</>
                  ) : (
                    <><Mic className="w-3.5 h-3.5 mr-1.5" /> Generate scene audio</>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 text-xs px-3 rounded-lg"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  disabled={generatingStorySfx || scenes.length === 0}
                  onClick={generateStorySoundEffects}
                >
                  {generatingStorySfx ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> SFX…</>
                  ) : (
                    <><Volume2 className="w-3.5 h-3.5 mr-1.5" /> AI SFX all scenes</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {isDialogueStory ? (
        <DialogueCameraDialog
          open={Boolean(dialogueCameraScene)}
          onOpenChange={(open) => { if (!open) setDialogueCameraSceneId(null); }}
          story={story}
          scene={dialogueCameraScene}
          characterById={characterById}
          pollJob={pollJob}
          onSceneChange={patchDialogueCameraScene}
          onStoryChange={patchDialogueCameraStory}
        />
      ) : null}

      <Dialog open={!!sceneAudioTarget} onOpenChange={(open) => { if (!open) setSceneAudioTarget(null); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto rounded-2xl" style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}>
          <DialogHeader>
            <DialogTitle className="text-base" style={{ color: 'var(--text-primary)' }}>
              Scene dialogue & audio
              {sceneAudioTarget ? ` — Scene ${scenes.findIndex((s) => s.id === sceneAudioTarget.id) + 1}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
            {isNarrationMode ? (
              <NarrationAudioSettingsPanel
                settings={narrationAudioSettings}
                onChange={onNarrationAudioSettingsChange}
                onChooseVoice={() => setNarrationVoicePickerOpen(true)}
                disabled={Boolean(sceneAudioGeneratingId)}
                testId="scene-audio-settings-panel"
              />
            ) : (
            <>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>Language</span>
              <select
                value={modalAudioLanguage}
                onChange={(e) => setModalAudioLanguage(e.target.value)}
                className="text-xs rounded-lg px-2 py-2 border"
                style={{
                  background: 'var(--bg-tertiary)',
                  borderColor: 'var(--glass-border)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="en">English</option>
                <option value="hi">Hindi</option>
                <option value="pa">Punjabi</option>
                <option value="bho">Bhojpuri</option>
                {!['en', 'hi', 'pa', 'bho'].includes(modalAudioLanguage) ? (
                  <option value={modalAudioLanguage}>{`Story language (${modalAudioLanguage})`}</option>
                ) : null}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>Narrator style</span>
              <select
                value={modalAudioVoice}
                onChange={(e) => setModalAudioVoice(e.target.value)}
                className="text-xs rounded-lg px-2 py-2 border"
                style={{
                  background: 'var(--bg-tertiary)',
                  borderColor: 'var(--glass-border)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="storyteller">Storyteller</option>
                <option value="female">Female narrator</option>
                <option value="male">Male narrator</option>
                <option value="child">Child narrator</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>Narration engine</span>
              <select
                value={modalNarratorTtsProvider}
                onChange={(e) => setModalNarratorTtsProvider(e.target.value)}
                className="text-xs rounded-lg px-2 py-2 border"
                style={{
                  background: 'var(--bg-tertiary)',
                  borderColor: 'var(--glass-border)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="auto">Auto (MiniMax → Murf → ElevenLabs)</option>
                <option value="minimax">Prefer MiniMax first</option>
                <option value="murf">Prefer Murf first (needs Murf voice ID)</option>
                <option value="elevenlabs">Prefer ElevenLabs first (needs voice ID)</option>
                <option value="runway">Runway ML audio</option>
              </select>
            </div>
            {modalNarratorTtsProvider === 'runway' && (
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>Runway audio model</span>
                <select
                  value={modalNarratorRunwayModel}
                  onChange={(e) => setModalNarratorRunwayModel(e.target.value)}
                  className="text-xs rounded-lg px-2 py-2 border"
                  style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                >
                  {runwayAudioModels.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>Narrator MiniMax voice ID (optional)</span>
              <input
                type="text"
                value={modalNarratorMinimax}
                onChange={(e) => setModalNarratorMinimax(e.target.value)}
                placeholder="Overrides default for this render"
                className="text-xs rounded-lg px-2 py-2 border w-full font-mono"
                style={{
                  background: 'var(--bg-tertiary)',
                  borderColor: 'var(--glass-border)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>Narrator Murf voice ID</span>
                <input
                  type="text"
                  value={modalNarratorMurf}
                  onChange={(e) => setModalNarratorMurf(e.target.value)}
                  placeholder="Murf voiceId"
                  className="text-xs rounded-lg px-2 py-2 border w-full font-mono"
                  style={{
                    background: 'var(--bg-tertiary)',
                    borderColor: 'var(--glass-border)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>Narrator Murf locale</span>
                <input
                  type="text"
                  value={modalNarratorMurfLocale}
                  onChange={(e) => setModalNarratorMurfLocale(e.target.value)}
                  placeholder="e.g. hi-IN (optional)"
                  className="text-xs rounded-lg px-2 py-2 border w-full font-mono"
                  style={{
                    background: 'var(--bg-tertiary)',
                    borderColor: 'var(--glass-border)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>Narrator ElevenLabs voice ID</span>
              <input
                type="text"
                value={modalNarratorEleven}
                onChange={(e) => setModalNarratorEleven(e.target.value)}
                placeholder="ElevenLabs voice_id"
                className="text-xs rounded-lg px-2 py-2 border w-full font-mono"
                style={{
                  background: 'var(--bg-tertiary)',
                  borderColor: 'var(--glass-border)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>Dialogue default (when character has no preference)</span>
              <select
                value={modalDialogueTtsDefault}
                onChange={(e) => setModalDialogueTtsDefault(e.target.value)}
                className="text-xs rounded-lg px-2 py-2 border"
                style={{
                  background: 'var(--bg-tertiary)',
                  borderColor: 'var(--glass-border)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="auto">Auto (from character / Murf if set)</option>
                <option value="minimax">After MiniMax, prefer standard fallbacks</option>
                <option value="murf">Prefer Murf after MiniMax</option>
                <option value="elevenlabs">Prefer ElevenLabs after MiniMax</option>
              </select>
            </div>
            </>
            )}
            {sceneAudioTarget && (
              <div className="space-y-3 pt-1 border-t" style={{ borderColor: 'var(--glass-border)' }}>
                <span className="text-[11px] font-medium block" style={{ color: 'var(--text-primary)' }}>
                  {isNarrationMode ? 'Edit narration script for this scene' : 'Edit lines and actions for this scene'}
                </span>
                {sceneScriptDraft.map((seg, idx) => (
                  <div key={`${idx}-${seg.kind}`} className="rounded-lg p-2 space-y-2" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={seg.kind}
                        onChange={(e) => {
                          const next = [...sceneScriptDraft];
                          next[idx] = { ...next[idx], kind: e.target.value };
                          setSceneScriptDraft(next);
                        }}
                        className="text-xs rounded-lg px-2 py-1.5 border w-full"
                        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                      >
                        <option value="dialogue">{SCRIPT_LINE_KIND_LABELS.dialogue}</option>
                        <option value="action">{SCRIPT_LINE_KIND_LABELS.action}</option>
                        <option value="narration">{SCRIPT_LINE_KIND_LABELS.narration}</option>
                      </select>
                      <select
                        value={seg.character_id || ''}
                        onChange={(e) => {
                          const cid = e.target.value;
                          const next = [...sceneScriptDraft];
                          next[idx] = { ...next[idx], character_id: cid, speaker: characterById[cid] || seg.speaker || '' };
                          setSceneScriptDraft(next);
                        }}
                        className="text-xs rounded-lg px-2 py-1.5 border w-full"
                        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                        disabled={seg.kind === 'narration'}
                      >
                        <option value="">{seg.kind === 'action' ? 'Select character' : 'Select speaker'}</option>
                        {(story?.characters || []).map((c) => (
                          <option key={c.id} value={c.id}>{c.name || c.id}</option>
                        ))}
                      </select>
                    </div>
                    <Textarea
                      rows={2}
                      value={seg.text || ''}
                      placeholder={
                        seg.kind === 'action'
                          ? 'What the character does on screen, e.g. Lounges lazily atop the fence'
                          : seg.kind === 'narration'
                            ? 'Narrator voice-over'
                            : 'Only the words spoken out loud'
                      }
                      onChange={(e) => {
                        const next = [...sceneScriptDraft];
                        next[idx] = { ...next[idx], text: e.target.value };
                        setSceneScriptDraft(next);
                      }}
                      className={`text-xs rounded-lg${seg.kind === 'action' ? ' italic' : ''}`}
                      style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                      data-testid={`scene-script-text-${idx}`}
                    />
                    {seg.kind === 'dialogue' && (
                      <Input
                        value={seg.action || ''}
                        placeholder="Acting while speaking (optional, not spoken), e.g. barking at a shadow"
                        onChange={(e) => {
                          const next = [...sceneScriptDraft];
                          next[idx] = { ...next[idx], action: e.target.value };
                          setSceneScriptDraft(next);
                        }}
                        className="h-8 text-[11px] rounded-lg italic"
                        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                        data-testid={`scene-script-acting-${idx}`}
                      />
                    )}
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-[11px]"
                        onClick={() => setSceneScriptDraft((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        Remove line
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full text-xs rounded-lg"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  onClick={() => setSceneScriptDraft((prev) => [...prev, { kind: 'dialogue', text: '', character_id: '', speaker: '' }])}
                >
                  Add line
                </Button>
                {Array.isArray(sceneAudioTarget.audio_timeline) && sceneAudioTarget.audio_timeline.length > 0 && (
                  <div className="space-y-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
                      Current timeline mapping
                    </span>
                    <ul className="max-h-24 overflow-y-auto space-y-1 text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                      {sceneAudioTarget.audio_timeline.map((seg, i) => (
                        <li key={`timeline-${i}`} className="flex justify-between gap-2">
                          <span className="truncate">
                            {seg.kind === 'narration' ? 'Narrator' : (characterById[seg.character_id] || seg.speaker || 'Dialogue')}
                            {seg.voice_id ? ` (${seg.voice_id})` : ''}: {seg.text}
                          </span>
                          {seg.segment_audio_url && (
                            <button
                              type="button"
                              className="underline"
                              style={{ color: 'var(--primary)' }}
                              onClick={() => {
                                const a = new Audio(mediaUrlForPlayback(seg.segment_audio_url));
                                a.play().catch(() => {});
                              }}
                            >
                              Play
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => setSceneAudioTarget(null)}
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}>
                Cancel
              </Button>
              <Button type="button" size="sm" className="rounded-lg" style={{ background: 'var(--primary)', color: 'white' }}
                onClick={submitSceneAudioGeneration}>
                <Mic className="w-3 h-3 mr-1" /> Generate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {isNarrationMode ? (
        <NarrationVoicePickerDialog
          open={narrationVoicePickerOpen}
          onOpenChange={setNarrationVoicePickerOpen}
          provider={storyTtsProvider}
          language={storyAudioLanguage}
          onSelectVoice={selectNarrationVoice}
        />
      ) : null}
    </div>
  );
};

/** Resolve segment image/video/audio URLs for SceneCard preview. */
function sceneCardMediaUrl(scene, field = 'image_url') {
  if (!scene) return '';
  const base = field.endsWith('_url') ? field.slice(0, -4) : field;
  const primary = field === 'image_url' ? narrationSceneImageUrl(scene) : scene[field];
  const s3Url = scene[`${base}_s3_url`];
  const localUrl = scene[`${base}_local_url`];
  let resolved = '';
  if (primary) {
    resolved = mediaUrlForPlayback(primary);
  }
  if (!resolved) {
    resolved = mediaUrlForPlayback(primary, { s3Url, localUrl }) || '';
  }
  if (field === 'image_url' && resolved) {
    return mediaUrlWithCacheBust(resolved, scene.updated_at || resolved);
  }
  return resolved;
}

const SceneCard = ({
  scene,
  sceneNumber,
  sceneIndex = 0,
  projectId = null,
  characterById,
  isNarrationMode = false,
  narrationVideoProvider,
  defaultVideoEngine,
  isEditing,
  isGeneratingImage,
  isGeneratingVideo,
  isSavingCameraDirection,
  isSavingImagePrompt,
  isLipSyncingVideo,
  isExpandingImage,
  isGeneratingSceneSfx,
  isGeneratingSceneAudio,
  isAdaptingScriptToVideo = false,
  adaptScriptRegenerateAudio = false,
  onAdaptScriptToVideo,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onRequestGenerateImage,
  onRequestGenerateVideo,
  onSaveCameraDirection,
  onSaveImagePrompt,
  onLipSyncVideo,
  onExpandImage,
  onGenerateSceneSfx,
  onSelectMedia,
  onUploadImage,
  onUploadVideo,
  onOpenDoodle,
  onOpenSceneAudio,
  onOpenDialogueCamera = null,
  isSelected = false,
  onToggleSelect,
  onDelete,
  deleteDisabled = false,
  isSongProject = false,
  multishotSelectionSaving = false,
  onMultishotMasterToggle,
  onMultishotShotToggle,
}) => {
  const [editText, setEditText] = useState(scene.scene_text || '');
  const [mediaView, setMediaView] = useState('image'); // 'image' | 'video'
  const initialVideoEngine = isNarrationMode && narrationVideoProvider === 'narration_slideshow'
    ? 'nano_banana'
    : normalizeVideoEngine(defaultVideoEngine);
  const [videoEngine, setVideoEngine] = useState(initialVideoEngine);
  const [cameraMotion, setCameraMotion] = useState('auto');
  const [cameraDirection, setCameraDirection] = useState(scene.camera_direction || '');
  const [imagePrompt, setImagePrompt] = useState(
    () => String(scene.visual || scene.image_prompt || '').trim(),
  );
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyKind, setHistoryKind] = useState('video');
  const [historyScene, setHistoryScene] = useState(scene);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandAspectRatio, setExpandAspectRatio] = useState('16:9');
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  useEffect(() => {
    if (isNarrationMode && narrationVideoProvider === 'narration_slideshow') {
      setVideoEngine('nano_banana');
    } else {
      setVideoEngine(normalizeVideoEngine(defaultVideoEngine));
    }
  }, [defaultVideoEngine, isNarrationMode, narrationVideoProvider]);

  const cameraChoices = CAMERA_MOTION_CHOICES[videoEngine] || CAMERA_MOTION_CHOICES.nano_banana;
  const cameraDirectionChoices =
    CAMERA_DIRECTION_PRESETS[videoEngine] || CAMERA_DIRECTION_PRESETS.nano_banana;
  useEffect(() => {
    if (!cameraChoices.find((opt) => opt.value === cameraMotion)) {
      setCameraMotion(cameraChoices[0]?.value || 'auto');
    }
  }, [videoEngine, cameraChoices, cameraMotion]);

  // Auto-switch to video tab when video becomes available
  useEffect(() => {
    if (scene.video_url) setMediaView('video');
  }, [scene.video_url]);

  useEffect(() => {
    setCameraDirection(scene.camera_direction || '');
  }, [scene.camera_direction, scene.id]);

  useEffect(() => {
    setImagePrompt(String(scene.visual || scene.image_prompt || '').trim());
  }, [scene.visual, scene.image_prompt, scene.id]);

  useEffect(() => {
    setHistoryScene(scene);
  }, [scene]);

  // Lean edit-scenes omit *_history — load versions when the dialog opens.
  useEffect(() => {
    if (!historyOpen || !isNarrationMode || !projectId || !scene?.id) return undefined;
    let cancelled = false;
    (async () => {
      setHistoryLoading(true);
      try {
        const { data } = await api.get(
          `/narration/projects/${projectId}/segments/${scene.id}/media-options`,
          { params: { kind: historyKind, include_all_versions: true, _fresh: Date.now() } },
        );
        if (cancelled) return;
        const options = Array.isArray(data?.options) ? data.options : [];
        const historyKey = historyKind === 'image'
          ? 'image_history'
          : historyKind === 'audio'
            ? 'audio_history'
            : 'video_history';
        const urlKey = `${historyKind}_url`;
        const active = options.find((opt) => opt?.active)?.url
          || data?.active_url
          || scene[urlKey];
        setHistoryScene((prev) => ({
          ...prev,
          ...scene,
          [urlKey]: active || prev[urlKey],
          [historyKey]: options
            .map((opt) => (typeof opt === 'string'
              ? { url: opt }
              : { url: opt?.url, created_at: opt?.created_at, source: opt?.source }))
            .filter((row) => row.url),
        }));
      } catch {
        if (!cancelled) setHistoryScene(scene);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [historyOpen, historyKind, isNarrationMode, projectId, scene]);

  const imageVersions = collectSceneMediaVersions(historyScene, 'image');
  const videoVersions = collectSceneMediaVersions(historyScene, 'video');
  const audioVersions = collectSceneMediaVersions(historyScene, 'audio');
  const currentVersions = historyKind === 'image'
    ? imageVersions
    : historyKind === 'audio'
      ? audioVersions
      : videoVersions;
  const audioTimeline = Array.isArray(scene.audio_timeline) ? scene.audio_timeline : [];
  const audioTimelineTotal =
    scene.audio_duration_seconds ||
    (audioTimeline.length ? audioTimeline[audioTimeline.length - 1]?.end_seconds : 0) ||
    1;
  const sceneActions = sceneActionLines(scene, characterById);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-panel rounded-2xl overflow-hidden flex flex-col"
      style={{ border: '1px solid var(--glass-border)' }}
    >
      {/* Scene Header */}
      <div className="px-4 pt-4 pb-3 border-b flex items-center justify-between"
        style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}>
        <div className="flex items-center gap-2 min-w-0">
          {onToggleSelect ? (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              disabled={deleteDisabled && !isSelected}
              className="h-4 w-4 rounded border shrink-0 cursor-pointer"
              style={{ borderColor: 'var(--glass-border)' }}
              data-testid={`scene-select-${scene.id}`}
              aria-label={`Select scene ${sceneNumber}`}
            />
          ) : null}
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full shrink-0"
            style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>
            Scene {sceneNumber}
          </span>
          <h3 className="font-semibold text-sm truncate" style={{ color: 'var(--text-primary)' }}>
            {scene.scene_title || scene.title || 'Scene'}
          </h3>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-2">
          {onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              disabled={deleteDisabled}
              className="p-1.5 rounded-lg transition-colors disabled:opacity-40"
              style={{ color: '#ef4444' }}
              title="Delete scene"
              data-testid={`scene-delete-${scene.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          ) : null}
          {/* Image / Video Toggle */}
          <div className="flex items-center rounded-full p-0.5"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}>
            <button
              onClick={() => setMediaView('image')}
              data-testid={`toggle-image-${scene.id}`}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all"
              style={{
                background: mediaView === 'image' ? 'var(--primary)' : 'transparent',
                color: mediaView === 'image' ? 'white' : 'var(--text-secondary)',
              }}
            >
              <ImageIcon className="w-3 h-3" /> Image
            </button>
            <button
              onClick={() => setMediaView('video')}
              data-testid={`toggle-video-${scene.id}`}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all"
              style={{
                background: mediaView === 'video' ? '#f97316' : 'transparent',
                color: mediaView === 'video' ? 'white' : 'var(--text-secondary)',
              }}
            >
              <Video className="w-3 h-3" /> Video
              {scene.video_url && (
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 ml-0.5" />
              )}
            </button>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setHistoryKind(mediaView === 'video' ? 'video' : 'image');
              setHistoryOpen(true);
            }}
            className="rounded-lg text-xs"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
            data-testid={`open-media-history-${scene.id}`}
          >
            <History className="w-3 h-3 mr-1" /> Versions
          </Button>
        </div>
      </div>

      {/* Media Preview */}
      <div className="relative aspect-video flex items-center justify-center overflow-hidden"
        style={{ background: 'var(--bg-tertiary)' }}>

        {mediaView === 'image' ? (
          sceneCardMediaUrl(scene, 'image_url') ? (
            <img
              key={sceneCardMediaUrl(scene, 'image_url')}
              src={sceneCardMediaUrl(scene, 'image_url')}
              alt={scene.scene_title || ''}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
              fetchPriority={sceneIndex < 2 ? 'high' : 'auto'}
            />
          ) : isGeneratingImage ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Generating image...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 cursor-pointer" onClick={onRequestGenerateImage}>
              <ImageIcon className="w-10 h-10" style={{ color: 'var(--text-tertiary)' }} />
              <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Click to generate</span>
            </div>
          )
        ) : isGeneratingVideo ? (
          <div className="flex flex-col items-center gap-2 w-full h-full justify-center">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#f97316' }} />
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Generating video...</span>
            {scene.video_url ? (
              <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>Updating clip when ready</span>
            ) : null}
          </div>
        ) : scene.video_url ? (
          <video
            key={scene.video_url}
            controls
            autoPlay={false}
            className="w-full h-full object-cover"
            data-testid={`scene-video-${scene.id}`}
          >
            <source src={sceneCardMediaUrl(scene, 'video_url')} type="video/mp4" />
          </video>
        ) : (
          <div className="flex flex-col items-center gap-2 cursor-pointer" onClick={() => onRequestGenerateVideo(cameraMotion)}>
            <Video className="w-10 h-10" style={{ color: 'var(--text-tertiary)' }} />
            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Click to generate</span>
          </div>
        )}
      </div>

      {sceneHasMultishotPicker(scene) ? (
        <div className="px-4 pt-3">
          <SongMultishotTimelinePicker
            scene={scene}
            disabled={multishotSelectionSaving}
            onToggleMaster={onMultishotMasterToggle}
            onToggleShot={onMultishotShotToggle}
          />
        </div>
      ) : null}

      {/* Scene Text */}
      <div className="p-4 flex-1 flex flex-col">
        {isEditing ? (
          <div className="space-y-3">
            <Textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={4}
              className="text-sm rounded-xl"
              style={{
                background: 'var(--bg-secondary)',
                borderColor: 'var(--glass-border)',
                color: 'var(--text-primary)'
              }}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => onSaveEdit(editText)} className="flex-1 rounded-lg"
                style={{ background: 'var(--primary)', color: 'white' }}>
                <Save className="w-3 h-3 mr-2" /> Save
              </Button>
              <Button size="sm" variant="outline" onClick={onCancelEdit} className="rounded-lg"
                style={{ borderColor: 'var(--glass-border)' }}>
                <X className="w-3 h-3" />
              </Button>
            </div>
            <div className="flex gap-2 items-center">
              <select
                value={expandAspectRatio}
                onChange={(e) => setExpandAspectRatio(e.target.value)}
                disabled={isExpandingImage}
                className="text-[11px] rounded-lg px-2 py-1.5 max-w-[9rem] border shrink-0"
                style={{
                  background: 'var(--bg-secondary)',
                  borderColor: 'var(--glass-border)',
                  color: 'var(--text-primary)',
                }}
                aria-label="Expand image aspect ratio"
              >
                <option value="16:9">16:9</option>
                <option value="9:16">9:16</option>
                <option value="1:1">1:1</option>
                <option value="4:3">4:3</option>
              </select>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onExpandImage(expandAspectRatio)}
                disabled={isGeneratingImage || isExpandingImage || !scene.image_url}
                className="flex-1 rounded-lg text-xs"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                title="Expand/resize current scene image with Runway and save as new image"
              >
                {isExpandingImage ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Expanding…</>
                ) : (
                  <><ImageIcon className="w-3 h-3 mr-1" /> Expand image</>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div onClick={onStartEdit}
            className="text-sm leading-relaxed cursor-pointer hover:opacity-80 line-clamp-3 group relative mb-4"
            style={{ color: 'var(--text-secondary)' }}>
            {scene.scene_text}
            <Edit2 className="w-3 h-3 absolute top-0 right-0 opacity-0 group-hover:opacity-100"
              style={{ color: 'var(--text-tertiary)' }} />
          </div>
        )}
        {isNarrationMode && onAdaptScriptToVideo && !isEditing ? (
          <Button
            size="sm"
            variant="outline"
            type="button"
            className="w-full rounded-lg text-xs mb-4 gap-1.5"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
            disabled={
              isAdaptingScriptToVideo
              || !sceneHasVideoForAdapt(scene)
              || !sceneScriptTextForAdapt(scene)
            }
            onClick={(e) => {
              e.stopPropagation();
              onAdaptScriptToVideo();
            }}
            title={
              sceneHasVideoForAdapt(scene)
                ? 'AI rewrites narration to fit full source video length (~2.5 words/sec), not timeline trim'
                : 'Generate video first'
            }
            data-testid={`adapt-script-to-video-${scene.id}`}
          >
            {isAdaptingScriptToVideo ? (
              <><Loader2 className="w-3 h-3 animate-spin" /> Matching script…</>
            ) : (
              <><TwinneeLogo className="w-3 h-3" /> Match script to video length</>
            )}
          </Button>
        ) : null}

        <div
          className="mb-4 rounded-xl p-3 space-y-2"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}
        >
          <div className="flex items-center justify-between gap-2">
            <span
              className="text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: 'var(--text-primary)' }}
            >
              Image prompt
            </span>
            <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
              Used for image generation
            </span>
          </div>
          <Textarea
            value={imagePrompt}
            onChange={(e) => setImagePrompt(e.target.value)}
            rows={4}
            placeholder="Describe the illustration for this scene — characters, setting, mood, camera angle…"
            className="text-xs rounded-lg resize-y min-h-[5rem]"
            style={{
              background: 'var(--bg-secondary)',
              borderColor: 'var(--glass-border)',
              color: 'var(--text-primary)',
            }}
            data-testid={`scene-image-prompt-${scene.id}`}
          />
          <Button
            size="sm"
            variant="outline"
            disabled={isSavingImagePrompt}
            onClick={() => onSaveImagePrompt(imagePrompt)}
            className="rounded-lg text-xs w-full"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
            data-testid={`save-image-prompt-${scene.id}`}
          >
            {isSavingImagePrompt ? (
              <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving…</>
            ) : (
              <><Save className="w-3 h-3 mr-1" /> Save image prompt</>
            )}
          </Button>
        </div>

        {sceneActions.length > 0 && (
          <div
            className="mb-4 rounded-xl p-3 space-y-1.5"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}
            data-testid={`scene-actions-${scene.id}`}
          >
            <span className="text-[11px] font-semibold uppercase tracking-wide block" style={{ color: 'var(--text-primary)' }}>
              Character actions <span className="normal-case font-normal" style={{ color: 'var(--text-tertiary)' }}>(animated, not spoken)</span>
            </span>
            <ul className="space-y-1 text-[10px]" style={{ color: 'var(--text-secondary)' }}>
              {sceneActions.map((line, i) => (
                <li key={`${scene.id}-action-${i}`} className="flex gap-2">
                  <span className="font-medium shrink-0" style={{ color: 'var(--text-primary)' }}>
                    {line.who}{line.whileSpeaking ? ' (while speaking)' : ''}:
                  </span>
                  <span className="italic">{line.text}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(scene.audio_url || audioTimeline.length > 0) && (
          <div className="mb-4 rounded-xl p-3 space-y-2"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-primary)' }}>
                Audio timeline
              </span>
              {audioVersions.length > 0 ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-lg text-[10px]"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  onClick={() => {
                    setHistoryKind('audio');
                    setHistoryOpen(true);
                  }}
                  data-testid={`open-audio-history-${scene.id}`}
                >
                  <History className="w-3 h-3 mr-1" />
                  Audio versions ({audioVersions.length})
                </Button>
              ) : null}
              {scene.audio_duration_seconds != null && (
                <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                  {Number(scene.audio_duration_seconds).toFixed(1)}s
                </span>
              )}
            </div>
            {scene.audio_url && (
              <audio
                controls
                className="w-full h-9"
                src={sceneCardMediaUrl(scene, 'audio_url')}
                data-testid={`scene-audio-mix-${scene.id}`}
              />
            )}
            {scene.sound_effect_url && (
              <div>
                <div className="text-[10px] mb-1" style={{ color: 'var(--text-tertiary)' }}>Scene sound effect</div>
                <audio controls className="w-full h-9" src={sceneCardMediaUrl(scene, 'sound_effect_url')} />
              </div>
            )}
            {audioTimeline.length > 0 && (
              <>
                <div className="flex h-2.5 w-full rounded-full overflow-hidden gap-px bg-black/10">
                  {audioTimeline.map((seg, si) => {
                    const w = Math.max(0.5, (seg.duration_seconds / audioTimelineTotal) * 100);
                    return (
                      <button
                        key={`${scene.id}-seg-${seg.index}-${si}`}
                        type="button"
                        title={`${seg.start_seconds}s–${seg.end_seconds}s · ${(seg.kind || '').toLowerCase() === 'narration' ? 'Narrator' : characterById[seg.character_id] || seg.speaker || 'Dialogue'}`}
                        className="h-full min-w-[2px] focus:outline-none focus:ring-1 focus:ring-white/80"
                        style={{
                          width: `${w}%`,
                          background: segmentBarColorCss(seg),
                        }}
                        onClick={() => {
                          if (!seg.segment_audio_url) return;
                          const a = new Audio(mediaUrlForPlayback(seg.segment_audio_url));
                          a.play().catch(() => {});
                        }}
                      />
                    );
                  })}
                </div>
                <ul className="max-h-24 overflow-y-auto space-y-1 text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                  {audioTimeline.map((seg, si) => {
                    const label =
                      (seg.kind || '').toLowerCase() === 'narration'
                        ? 'Narrator'
                        : characterById[seg.character_id] || seg.speaker || 'Dialogue';
                    return (
                      <li key={`${scene.id}-line-${si}`} className="flex gap-2 justify-between">
                        <span className="shrink-0 font-mono text-[9px] opacity-80">
                          {seg.start_seconds}s
                        </span>
                        <span className="flex-1 truncate">
                          <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{label}:</span>{' '}
                          {seg.text}
                        </span>
                        {seg.segment_audio_url && (
                          <button
                            type="button"
                            className="shrink-0 underline text-[9px]"
                            style={{ color: 'var(--primary)' }}
                            onClick={() => {
                              const a = new Audio(mediaUrlForPlayback(seg.segment_audio_url));
                              a.play().catch(() => {});
                            }}
                          >
                            Play clip
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        )}

        {/* Action Buttons */}
        {!isEditing && (
          <div className="mt-auto space-y-2">
            {/* Image Actions */}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={onRequestGenerateImage}
                disabled={isGeneratingImage} className="flex-1 rounded-lg text-xs"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                data-testid={`generate-image-btn-${scene.id}`}>
                {isGeneratingImage ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Working...</>
                ) : (
                  <><TwinneeLogo className="w-3 h-3 mr-1" />{scene.image_url ? 'Regen' : 'Gen Image'}</>
                )}
              </Button>
              <Button size="sm" variant="outline" onClick={onOpenDoodle}
                disabled={isGeneratingImage || isNarrationMode} className="rounded-lg text-xs"
                style={{ borderColor: 'var(--glass-border)', color: '#8B5CF6' }}
                title={isNarrationMode ? 'Draw is not available for narration segments' : 'Draw a doodle to convert to image'}
                data-testid={`doodle-btn-${scene.id}`}>
                <Pencil className="w-3 h-3 mr-1" /> Draw
              </Button>
              <Button size="sm" variant="outline" onClick={() => imageInputRef.current?.click()}
                disabled={isGeneratingImage} className="rounded-lg" title="Upload image"
                style={{ borderColor: 'var(--glass-border)' }}>
                <Upload className="w-3 h-3" />
              </Button>
              <input ref={imageInputRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadImage(f); }} />
            </div>

            {/* Video Actions */}
            <div className="flex flex-col gap-2">
              <div className="flex gap-2 items-center flex-wrap">
<select
                  value={cameraMotion}
                  onChange={(e) => setCameraMotion(e.target.value)}
                  disabled={isGeneratingVideo}
                  className="text-[11px] rounded-lg px-2 py-1.5 max-w-[11rem] border shrink-0"
                  style={{
                    background: 'var(--bg-secondary)',
                    borderColor: 'var(--glass-border)',
                    color: 'var(--text-primary)',
                  }}
                  data-testid={`scene-camera-motion-${scene.id}`}
                  aria-label="Camera motion"
                >
                  {cameraChoices.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={() => onRequestGenerateVideo(cameraMotion)}
                  disabled={isGeneratingVideo}
                  className="flex-1 rounded-lg text-xs min-w-0"
                  style={{ background: '#f97316', color: 'white' }}
                  data-testid={`generate-video-btn-${scene.id}`}
                >
                  {isGeneratingVideo ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating...</>
                  ) : (
                    <><Video className="w-3 h-3 mr-1 shrink-0" />{scene.video_url ? 'Regen' : 'Gen'} video</>
                  )}
                </Button>
                <Button size="sm" variant="outline" onClick={() => videoInputRef.current?.click()}
                  disabled={isGeneratingVideo}
                  className="rounded-lg shrink-0" title="Upload video"
                  style={{ borderColor: 'var(--glass-border)' }}>
                  <Upload className="w-3 h-3" />
                </Button>
                <input ref={videoInputRef} type="file" accept="video/*" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadVideo(f); }} />
              </div>
              <div className="flex gap-2 items-center">
                <select
                  value={
                    cameraDirectionChoices.some((o) => o.value === cameraDirection)
                      ? cameraDirection
                      : ''
                  }
                  onChange={(e) => {
                    const next = e.target.value || '';
                    if (!next) return;
                    setCameraDirection(next);
                  }}
                  className="text-[11px] rounded-lg px-2 py-1.5 max-w-[12rem] border shrink-0"
                  style={{
                    background: 'var(--bg-secondary)',
                    borderColor: 'var(--glass-border)',
                    color: 'var(--text-primary)',
                  }}
                  aria-label="Camera angle preset"
                  title="Pick a camera angle/movement preset (provider-specific). You can still edit the text field."
                >
                  {cameraDirectionChoices.map((opt) => (
                    <option key={opt.label} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={cameraDirection}
                  onChange={(e) => setCameraDirection(e.target.value)}
                  placeholder="[Pan left], [Pedestal up], low-angle shot, 35mm lens"
                  className="text-[11px] rounded-lg px-2 py-1.5 border flex-1 min-w-0"
                  style={{
                    background: 'var(--bg-secondary)',
                    borderColor: 'var(--glass-border)',
                    color: 'var(--text-primary)',
                  }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isSavingCameraDirection}
                  onClick={() => onSaveCameraDirection(cameraDirection)}
                  className="rounded-lg text-xs"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  title="Save per-scene camera angle/movement directives for image+video generation"
                >
                  {isSavingCameraDirection ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving…</>
                  ) : (
                    <>Save camera angle</>
                  )}
                </Button>
              </div>
              <p className="text-[10px] leading-snug" style={{ color: 'var(--text-tertiary)' }}>
                Story default:{' '}
                {defaultVideoEngine === 'minimax'
                  ? 'MiniMax'
                  : defaultVideoEngine === 'veo3'
                    ? 'Veo 3 (Gemini)'
                    : defaultVideoEngine === 'runway'
                      ? 'Runway ML'
                      : defaultVideoEngine === 'seedance'
                        ? 'Seedance 2'
                      : defaultVideoEngine === 'ltx'
                        ? 'LTX'
                        : 'Nano Banana'}
                . Camera motion choices update with the selected model.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onLipSyncVideo(videoEngine, cameraMotion)}
                disabled={isLipSyncingVideo || !scene.video_url || !scene.audio_url}
                className="rounded-lg text-xs w-full"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                title="Regenerate a lip-synced video using this scene's audio"
              >
                {isLipSyncingVideo ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Lip sync…</>
                ) : (
                  <><Video className="w-3 h-3 mr-1" /> Lip sync video with audio</>
                )}
              </Button>
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={onOpenSceneAudio}
                disabled={isGeneratingSceneAudio}
                className="flex-1 rounded-lg text-xs"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                data-testid={`generate-scene-audio-btn-${scene.id}`}
              >
                {isGeneratingSceneAudio ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Audio…</>
                ) : (
                  <><Mic className="w-3 h-3 mr-1" /> Scene dialogue</>
                )}
              </Button>
              {onOpenDialogueCamera ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onOpenDialogueCamera}
                  className="flex-1 rounded-lg text-xs"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  title="Who the camera shows for each line"
                  data-testid={`dialogue-camera-btn-${scene.id}`}
                >
                  <Clapperboard className="w-3 h-3 mr-1" /> Camera
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="outline"
                onClick={onGenerateSceneSfx}
                disabled={isGeneratingSceneSfx}
                className="flex-1 rounded-lg text-xs"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
              >
                {isGeneratingSceneSfx ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> SFX…</>
                ) : (
                  <><Volume2 className="w-3 h-3 mr-1" /> AI sound effect</>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5 pb-3 border-b" style={{ borderColor: 'var(--glass-border)' }}>
            <DialogTitle className="text-base" style={{ color: 'var(--text-primary)' }}>
              Scene {sceneNumber} Media Versions
            </DialogTitle>
            <div className="flex items-center gap-2 mt-2">
              <button
                type="button"
                onClick={() => setHistoryKind('image')}
                className="px-3 py-1.5 rounded-full text-xs font-medium"
                style={{
                  background: historyKind === 'image' ? 'var(--primary)' : 'var(--bg-tertiary)',
                  color: historyKind === 'image' ? 'white' : 'var(--text-secondary)',
                }}
              >
                Image history ({imageVersions.length})
              </button>
              <button
                type="button"
                onClick={() => setHistoryKind('video')}
                className="px-3 py-1.5 rounded-full text-xs font-medium"
                style={{
                  background: historyKind === 'video' ? '#f97316' : 'var(--bg-tertiary)',
                  color: historyKind === 'video' ? 'white' : 'var(--text-secondary)',
                }}
              >
                Video history ({videoVersions.length})
              </button>
              <button
                type="button"
                onClick={() => setHistoryKind('audio')}
                className="px-3 py-1.5 rounded-full text-xs font-medium"
                style={{
                  background: historyKind === 'audio' ? '#ec4899' : 'var(--bg-tertiary)',
                  color: historyKind === 'audio' ? 'white' : 'var(--text-secondary)',
                }}
              >
                Audio history ({audioVersions.length})
              </button>
              <div className="ml-auto">
                {historyKind === 'image' ? (
                  <Button
                    size="sm"
                    onClick={onRequestGenerateImage}
                    disabled={isGeneratingImage}
                    className="rounded-lg text-xs"
                    style={{ background: 'var(--primary)', color: 'white' }}
                  >
                    {isGeneratingImage ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <TwinneeLogo className="w-3 h-3 mr-1" />}
                    Regenerate image
                  </Button>
                ) : historyKind === 'audio' ? (
                  <Button
                    size="sm"
                    onClick={onOpenSceneAudio}
                    disabled={isGeneratingSceneAudio}
                    className="rounded-lg text-xs"
                    style={{ background: '#ec4899', color: 'white' }}
                  >
                    {isGeneratingSceneAudio ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mic className="w-3 h-3 mr-1" />}
                    Regenerate audio
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => onRequestGenerateVideo(cameraMotion)}
                    disabled={isGeneratingVideo}
                    className="rounded-lg text-xs"
                    style={{ background: '#f97316', color: 'white' }}
                  >
                    {isGeneratingVideo ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Video className="w-3 h-3 mr-1" />}
                    Regenerate video
                  </Button>
                )}
              </div>
            </div>
          </DialogHeader>

          <div className="max-h-[65vh] overflow-y-auto p-4" style={{ background: 'var(--bg-primary)' }}>
            {historyLoading ? (
              <div className="py-12 flex items-center justify-center gap-2 text-sm" style={{ color: 'var(--text-tertiary)' }}>
                <Loader2 className="w-4 h-4 animate-spin text-[#62FAE3]" />
                Loading versions…
              </div>
            ) : currentVersions.length === 0 ? (
              <div className="py-12 text-center text-sm" style={{ color: 'var(--text-tertiary)' }}>
                No {historyKind} versions yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {currentVersions.map((version, idx) => (
                  <button
                    key={`${historyKind}-${version.url}-${idx}`}
                    type="button"
                    onClick={() => {
                      onSelectMedia(historyKind, version.url);
                      setHistoryOpen(false);
                    }}
                    className="text-left rounded-xl overflow-hidden border transition-all hover:translate-y-[-1px]"
                    style={{
                      borderColor:
                        historyKind === 'image'
                          ? (version.url === scene.image_url ? 'hsl(var(--primary))' : 'var(--glass-border)')
                          : historyKind === 'audio'
                            ? (version.url === scene.audio_url ? '#ec4899' : 'var(--glass-border)')
                            : (version.url === scene.video_url ? '#f97316' : 'var(--glass-border)'),
                      background: 'var(--bg-secondary)',
                    }}
                  >
                    <div
                      className={historyKind === 'audio' ? 'p-4 flex flex-col items-center justify-center min-h-[5rem]' : 'aspect-video'}
                      style={{ background: 'var(--bg-tertiary)' }}
                    >
                      {historyKind === 'image' ? (
                        <img src={mediaUrlForPlayback(version.url)} alt="" className="w-full h-full object-cover" />
                      ) : historyKind === 'audio' ? (
                        <>
                          <Mic className="w-8 h-8 mb-2" style={{ color: '#ec4899' }} />
                          <audio
                            src={mediaUrlForPlayback(version.url)}
                            controls
                            className="w-full h-9"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </>
                      ) : (
                        <video
                          src={mediaUrlForPlayback(version.url)}
                          muted
                          playsInline
                          preload="metadata"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="px-2.5 py-2 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                          {version.isActive ? 'Currently selected' : `Version ${currentVersions.length - idx}`}
                        </span>
                        {version.isActive && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                        {version.createdAt ? new Date(version.createdAt).toLocaleString() : 'Older version'}
                      </p>
                      {version.source && (
                        <p className="text-[10px] truncate" style={{ color: 'var(--text-tertiary)' }}>
                          Source: {version.source}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default SceneGridEditor;
